import Vue from 'vue';
import isNumber from "lodash/isNumber";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import { generateUUID, parseDeliveryAddress, getUrlParameter, findErrorMessage, isNullOrEmpty, isTimeStale, endSentence } from "@/services/util.service"
import { isInSchedule } from "@/services/schedule.service"

import { loadStripe } from '@stripe/stripe-js';
import { load } from 'recaptcha-v3'
import moment from "moment-timezone"

const checkoutFormBlank = {
  email: '',
  // No email present here -- default to unchecked, same as a blank checkout page.
  emailReceipt: false,
  firstname: '',
  lastname: '',
  orderNotes: '',
  paymentMethods: Array[0],
  phoneNumber: '',
  scheduledTime: '',
  selectedPayMethodId: null,
  tipsAmount: 0,
  takeout: null
}

const ORDER_TYPES = [
  {
    text: 'Delivery',
    i18n: '$vuetify.cart.delivery',
    value: 'DELIVERY',
    key: 'deliverySupported'
  },
  {
    text: 'Pick Up',
    i18n: '$vuetify.cart.pickup',
    value: 'PICKUP',
    key: 'pickupSupported'
  },
  {
    text: 'Dine-In',
    i18n: '$vuetify.cart.dinein',
    value: 'DINE_IN',
    key: 'dineInSupported'
  }
]


const USER_INPUT_FIELDS = [
  'uuid',
  'code',
  'errors',
  'childItemList',
  'relations',
  'qty',
  'itemAttributeList',
  'specialRequest',
  'minCardinality'
]

const filterUserInput = (item) => {
  let obj = {};
  for (let key of USER_INPUT_FIELDS)
    obj[key] = item[key];
  return obj;
}

function getActiveMultiOrder(state) {
  if (state.orderType !== 'DINE_IN') return null
  if (isEmpty(state.multiOrder)) return null
  return state.multiOrder
}

function getCurrentTableNo(state, getters) {
  return state.orderType === 'DINE_IN' ? getters.getTableId : -1
}

let compareTo = (o1, o2) => {
  let o1Priority = 0, o2Priority = 0;
  if (o1.forMembership)
    o1Priority += 100;
  if (o1.promotional)
    o1Priority += 10;
  if (o2.forMembership)
    o2Priority += 100;
  if (o2.promotional)
    o2Priority += 10;
  if (!isNullOrEmpty(o2.orderTypes))
    o2Priority += 1000;
  if (!isNullOrEmpty(o1.orderTypes))
    o1Priority += 1000;
  if (!isNullOrEmpty(o2.channels))
    o2Priority += 10000;
  if (!isNullOrEmpty(o1.channels))
    o1Priority += 10000;
  if (!isNullOrEmpty(o1.valueDependency))
    o1Priority += 100000;
  if (!isNullOrEmpty(o2.valueDependency))
    o2Priority += 100000;

  if (o2Priority != o1Priority) {
    return o2Priority - o1Priority;
  } else {
    if (o1.price < o2.price)
      return -1;
    else if (o1.price > o2.price)
      return 1;
    else
      return 0;
  }
}

// GA4 item for a local cart entry: English name and base unit price
const toGa4Item = (getters, cartItem) => {
  const item = cartItem.menuItem || cartItem
  const fullItem = getters.findFullItem ? getters.findFullItem(cartItem) : item
  // Lines synced from the server cart carry name: undefined + nameTranslation, which hide the menu name
  const en = (fullItem.translations || get(fullItem, 'nameTranslation.translations', [])).find(t => t.lang === 'en')
  return {
    item_id: String(item.code || ''),
    item_name: (en && en.value) || fullItem.name || '',
    price: Number(fullItem.price || (Array.isArray(fullItem.charges) && fullItem.charges[0] && fullItem.charges[0].price) || item.price || 0),
    quantity: Number(item.qty || 1)
  }
}

const chargeDependenciesPass = (charge, _item) => {
  if (isNullOrEmpty(charge.valueDependency)) {
    return true;
  }
  if (_item && !isNullOrEmpty(_item.itemAttributeList)) {
    const _passedValueDependencies = [];

    charge.valueDependency.forEach((_vd) => {
      const _passedDependency = !!(
        _item.itemAttributeList.find(
          (_modifier) => _modifier.code == _vd.attributeCode
        ) || { attrValueList: [] }
      ).attrValueList.find((_value) => _value.code == _vd.valueCode);

      // If the value is selected, the valueDependency passed
      if (!!_passedDependency) _passedValueDependencies.push(_vd);
    });
    // If there are valueDependencies, skip this charge if any didn't pass
    return _passedValueDependencies.length == charge.valueDependency.length;
  }
  return false;
};

function areItemsEqualIgnoringUuid(item1, item2) {
  const copy1 = {
    ...item1,
    relationName: undefined,
    uuid: undefined,
    qty: undefined
  };
  const copy2 = {
    ...item2,
    relationName: undefined,
    uuid: undefined,
    qty: undefined
  };
  if (item2.attributes && item2.attributes.Configurable || item2.specialRequest) {
    return false
  } else {
    return JSON.stringify(copy1) === JSON.stringify(copy2);
  }
}

function combineAndRemoveDuplicates(arr) {
  const result = [];

  arr.forEach((item) => {
    const existingItemIndex = result.findIndex((existing) =>
      areItemsEqualIgnoringUuid(existing, item)
    );
    if (existingItemIndex !== -1) {
      result[existingItemIndex].qty += item.qty || 1;
    } else {
      result.push(item);
    }
  });

  return result;
}

const filterEmptyAttributes = (attributes) => {
  if (isNullOrEmpty(attributes))
    return []
  return attributes.filter((_attribute) => !isNullOrEmpty(_attribute.attrValueList))
}
const buildMenuStructure = (cartItem) => {
  const buildRelational = (cartItem) => {
    const createRelation = (item, relation) => {
      return {
        // Relation item wrapper
        code: get(relation, 'code', -1),
        qty: get(item, 'qty', -1),
        name: get(relation, 'name', ''),
        relation: true,

        childItemList: [{
          code: get(item, 'code', -1),
          qty: get(item, 'qty', -1),
          name: get(item, 'name', ''),
          itemAttributeList: filterEmptyAttributes(get(item, 'itemAttributeList', [])),
          specialRequest: get(item, 'specialRequest', ''),
          childItemList: [],
          relation: false,
          uuid: generateUUID()  // Is this needed?
        }]
      }
    }

    return {
      // Base Menu item wrapper
      code: get(cartItem, 'code', -1),
      qty: get(cartItem, 'qty', -1),
      relation: false,
      specialRequest: get(cartItem, 'specialRequest', ''),
      itemAttributeList: filterEmptyAttributes(get(cartItem, 'itemAttributeList', [])),

      childItemList: get(cartItem, 'childItemList', []), //.map((child, i) => createRelation(child, cartItem.relations[i])),

      uuid: get(cartItem, 'uuid', -1)  // Is this needed?
    }
  }

  const buildMenuItem = (cartItem) => {
    return {
      code: cartItem.code,
      name: cartItem.name,
      qty: cartItem.qty,
      childItemList: cartItem.childItemList || [],
      itemAttributeList: filterEmptyAttributes(cartItem.itemAttributeList),
      relation: false,
      specialRequest: _.get(cartItem, 'specialRequest', '')
    }
  }

  if ((cartItem.relations && !!cartItem.relations.length)) {
    return buildRelational(cartItem)
  } else {
    return buildMenuItem(cartItem)
  }
}

const buildSharedCartItem = (menuItem) => {
  return {
    code: menuItem.code || '',
    name: menuItem.name || menuItem.nameTranslation || '',
    price: menuItem.itemPrice || menuItem.price || 0,
    qty: menuItem.qty || 1,
    itemAttributeList: menuItem.itemAttributeList || [],
    childItemList: menuItem.childItemList || [],
    relations: menuItem.relations || [],
    specialRequest: menuItem.specialRequest || ''
  }
}

// Shared across concurrent fetchShoppingCart() calls with the SAME options shape —
// { promise, trailing } per key; see the fetchShoppingCart action for why this exists. Keyed by a
// signature of the options (bare calls all share the '' key) so two calls that need
// genuinely different results (e.g. different orderTypeOverride) never collide.
let _inFlightFetchShoppingCart = {}

// checkStaleScheduledTime's { throttle: true } window -- see that action's comment.
const STALE_SCHEDULE_THROTTLE_MS = 60000
let _lastStaleScheduleCheckAt = 0

// fetchBillView/fetchGroupOrderBillView are each dispatched from several independent
// triggers (checkout mount, router.afterEach's host cart lock, coupon apply/remove,
// PaymentCard's wallet-pay refresh, the periodic-less-now but still real re-checks) --
// overlapping calls are routine, not an edge case. A slower, earlier request finishing
// AFTER a faster, later one would otherwise be free to commit its (now-stale) result --
// including SET_BILL_LOAD_ERROR(true) clobbering a valid bill the newer request already
// committed. Shared token: each call stamps its own request number, and only the call
// still holding the latest token when its promise settles is allowed to commit.
let _billRequestToken = 0

const buildRedemptionItems = (rewardOptions) => {
  if (!!rewardOptions) {
    const relations = get(rewardOptions, 'menuItem.itemRelationList', [])
    const children = get(rewardOptions, 'childItemList', [])

    const parseChild = (menuItem, relation) => {
      return {
        code: get(relation, 'code', 0),
        qty: get(menuItem, 'qty', null) || 1,
        relation: true,
        childItemList: [{
          code: get(menuItem, 'raw.code', null),
          qty: get(menuItem, 'qty', null) || 1,
          specialRequest: get(menuItem, 'specialRequest', null),
          itemAttributeList: filterEmptyAttributes(get(menuItem, 'itemAttributeList', null)),
          childItemList: [],
          relation: false,
          uuid: generateUUID()
        }]
      }
    }

    if ('menuItem' in rewardOptions) {
      return [{
         code: rewardOptions.id,
         qty : 1,
         orderItem: {
           code: rewardOptions.id,
           qty: 1,
           relation: false,
           childItemList: children.map((child, index) => parseChild(child, relations[index]))
         }
       }]
    } else {
      return [{
         code: rewardOptions.id,
         qty : 1
       }]
    }
 }

  return []
}

const parseCoupons = (coupons) => {
  return coupons.map(coupon => {
    return {
      code: get(coupon, 'code'),
      uuid: get(coupon, 'uuid'),
      qty : get(coupon, 'qty', 1),
      orderItem: get(coupon, 'orderItem'),
      purchase: get(coupon, 'purchase')
    }
  })
}

const isSelectedMethod = (method, getters) => {
  if (typeof getters.getPaymentType == 'object' && getters.getPaymentType.length) {
    return getters.getPaymentType.includes(method)
  } else {
    return false
  }
}

export default {
  state: {
    isBillLoading: false,
    isBillLoadError: false,
    isCartLoading: false,
    carts: {},
    localCart: [],
    serverCart: {},
    bill: {},
    orderType: '',
    tableId: -1, // default value that indicates we do not use a table
    tableDisplay: '',
    tableSetAt: -1,
    enteredViaQr: false,
    deliveryAddress: null,
    deliveryAddressResult: null, // Detailed object from Google with more info if needed; UI uses the formatted_address
    deliveryProvider: {},
    deliveryProvidersDetails: [],
    isAnimating: false,
    multiOrder: {},
    subOrder: {},
    paymentType: null,
    inStore: {},
    serviceCharges: [],
    couponCodes: {}, // per store
    // DEV-10377: the most Membership Balance the guest chose to use on this order ("Apply $X
    // instead"), in dollars. null = use as much as the order needs (the default).
    membershipPayCap: null,

    rewards: [],
    rewardsConfig: null,
    activeRewards: null,
    promotions: [],
    // Pre-computed promotion lookup maps for O(1) access
    promotionsByItemCode: {},
    promotionsByTag: {},
    promotionsByCategory: {}
  },
  getters: {
    // We want to only store the user inputed fields in state
    // And find the original item in the menu to combine the api provided fields
    // We do this to reduce state information and grab any updates to the item
    findFullItem: (state, getters) => (item) => {
      if ('menuItem' in item)
        item = item.menuItem

      const searchForItem = (item) => {
        if (!!(getters.getAllMenus && getters.getAllMenus.length && item.code)) {
          for (const menu of getters.getAllMenus)
            for (const cat of menu.menuGroups)
              for (const menuItem of cat.menuItems)
                if (menuItem.code == item.code)
                  return menuItem
        }

        return item
      }

      const serverItem = searchForItem(item)

      if (!!serverItem) {
        return {
          ...serverItem,
          ...item
        }
      } else {
        return item
      }
    },



    doesLocalCartHaveErrors(state, getters) {
      const localCart = getters.getLocalCart

      let hasErrors = false
      localCart.forEach(({ menuItem }) => {
        if (Array.isArray(menuItem.errors) && menuItem.errors.length > 0) {
          hasErrors = true
        }
      })

      return hasErrors
    },

    getDeliveryMin(state, getters) {
      return get(getters, 'getStore.settings.deliverySettings.minimumOrderValueInCents', 0) / 100
    },
    doesCartMeetDeliveryMinimum(state, getters, rootState, rootGetters) {
      if (getters.getOrderType !== 'DELIVERY')
        return true

      // Group orders: the host's own sub-order can be empty while other members
      // have already added items, so check the group's combined subtotal instead
      // of just the host's individual cart.
      if (rootGetters['groupOrder/isInGroupOrder']) {
        const groupSubTotal = rootGetters['groupOrder/groupSubTotal'] || 0
        if (!groupSubTotal) return true
        return groupSubTotal >= getters.getDeliveryMin
      }

      const orders = get(getters.getServerCart, 'subOrderList[0].orderItemList', [])

      // If the current ordery doesn't meet basic order restrictions just allow
      if (!orders.length)
        return true

        // ORD_ERR_0027 is reserved for delivery does not meet minimum error
        // If this error does not exist, we have met the minimum
        const cartErrors = get(getters.getServerCart, 'validationErrors', [])
        return cartErrors.findIndex(error => error.errorCode == 'ORD_ERR_0027') < 0
    },

    getPickupMin(state, getters) {
      return get(getters, 'getStore.settings.pickupSettings.minimumOrderValueInCents', 0) / 100
    },
    doesCartMeetPickupMinimum(state, getters, rootState, rootGetters) {
      if (getters.getOrderType !== 'PICKUP')
        return true

      // Group orders: check the group's combined subtotal, not just the host's
      // individual cart (see doesCartMeetDeliveryMinimum for why).
      if (rootGetters['groupOrder/isInGroupOrder']) {
        const groupSubTotal = rootGetters['groupOrder/groupSubTotal'] || 0
        if (!groupSubTotal) return true
        return groupSubTotal >= getters.getPickupMin
      }

      const orders = get(getters.getServerCart, 'subOrderList[0].orderItemList', [])

      // If the current ordery doesn't meet basic order restrictions just allow
      if (!orders.length)
        return true

      // ORD_ERR_0039 is reserved for pickup does not meet minimum error
      // If this error does not exist, we have met the minimum
      const cartErrors = get(getters.getServerCart, 'validationErrors', [])
      return cartErrors.findIndex(error => error.errorCode == 'ORD_ERR_0039') < 0
    },
    cartValidationErrors(state, getters, rootState, rootGetters) {
      let errors = get(getters.getServerCart, 'validationErrors', [])

      // The order minimums (ORD_ERR_0027 delivery / ORD_ERR_0039 pickup) are reported by
      // doesCartMeetOrderMinimum for the CURRENT order type (group-total-aware), so drop
      // them here. Otherwise a server cart priced for the other type keeps showing its
      // minimum: DEV-11365, Pickup -> Delivery -> invalid address -> cancel left the
      // Delivery cart's "eligible for delivery" error on a Pickup order.
      errors = errors.filter(error => error.errorCode !== 'ORD_ERR_0027' && error.errorCode !== 'ORD_ERR_0039')

      // A validation error here always means some ITEM is blocking the cart (out of
      // stock, disabled, invalid selection) -- name the fix, not just the problem.
      // Otherwise clicking + on an unrelated item silently no-ops with only "X is out
      // of stock" showing, and it's not obvious that's why THIS click did nothing.
      const suffix = Vue.prototype.$vuetify.t('$vuetify.cart.removeInvalidItemToContinue')
      return errors.map((_error) => endSentence(_error.message) + ' ' + suffix).join(',<br>')
    },

    getOrderMinimum(state, getters) {
      if (getters.getOrderType == 'DELIVERY')
        return getters.getDeliveryMin
      if (getters.getOrderType == 'PICKUP')
        return getters.getPickupMin
      return null
    },
    doesCartMeetOrderMinimum(state, getters) {
      if (getters.getOrderType == 'DELIVERY')
        return getters.doesCartMeetDeliveryMinimum
      if (getters.getOrderType == 'PICKUP')
        return getters.doesCartMeetPickupMinimum

      // No minimum for dine in
      return true
    },

    isBillLoading(state) {
      return state.isBillLoading;
    },
    isBillLoadError(state) {
      return state.isBillLoadError;
    },
    getCouponCode(state, getters, rootState, rootGetters) {
      const storeId = rootGetters.getUniqueStoreId
      return state.couponCodes[storeId]
    },
    getAllCartCoupons(state){
      const subOrders = get(state, 'serverCart.subOrderList', [])

      let allCoupons = []
      subOrders.forEach(subOrder => {
        const coupons = get(subOrder, 'coupons', [])

        allCoupons = [ ...allCoupons, ...coupons]
      })

      return allCoupons
    },
    getAllBillCoupons(state){
      const subOrders = get(state, 'bill.order.subOrderList', [])

      let allCoupons = []
      subOrders.forEach(subOrder => {
        const coupons = get(subOrder, 'coupons', [])

        allCoupons = [ ...allCoupons, ...coupons]
      })

      return allCoupons
    },
    hasAnyCouponAppliedToCart(state) {
      const subOrders = get(state, 'serverCart.subOrderList', [])

      let hasAnyCoupon = false
      subOrders.forEach(subOrder => {
        if (!hasAnyCoupon) {
          const coupons = get(subOrder, 'coupons', [])

          hasAnyCoupon = coupons.length > 0
        }
      })

      return hasAnyCoupon
    },
    hasAnyCouponAppliedToBill(state) {
      const subOrders = get(state, 'bill.order.subOrderList', [])

      let hasAnyCoupon = false
      subOrders.forEach(subOrder => {
        if (!hasAnyCoupon) {
          const coupons = get(subOrder, 'coupons', [])

          hasAnyCoupon = coupons.length > 0
        }
      })

      return hasAnyCoupon
    },
    hasValidCouponAppliedToCart(state) {
      const subOrders = get(state, 'serverCart.subOrderList', [])

      let hasValidCoupon = false
      subOrders.forEach(subOrder => {
        if (!hasValidCoupon) {
          const coupons = get(subOrder, 'coupons', [])

          const eligibleCouponIndex = coupons.findIndex(coupon => get(coupon, 'ruleResult.isEligible') === true)

          hasValidCoupon = eligibleCouponIndex !== -1
        }
      })

      return hasValidCoupon
    },
    OrderTypeSpecificCouponAppliedToCart(state) {
      const subOrders = get(state, 'serverCart.subOrderList', [])

      let orderTypes = []
      subOrders.forEach(subOrder => {
        const coupons = get(subOrder, 'coupons', [])

        coupons.forEach(coupon => {
          if (coupon.allowedPaymentTypes && coupon.allowedPaymentTypes.length) {
            orderTypes = [
              ...orderTypes,
              ...(coupon.allowedPaymentTypes || [])
            ]
          }
        });
      })

      return orderTypes.map(orderType => orderType.toLowerCase())
    },
    getCouponErrors(state, getters) {
      if (!getters.hasValidCouponAppliedToCart) {
        let errors = ''
        const subOrders = get(state, 'serverCart.subOrderList', [])

        subOrders.forEach(subOrder => {
          const coupons = get(subOrder, 'coupons', [])

          for (let coupon of coupons) {
            get(coupon, 'ruleResult.ruleResults', []).forEach(rule => {
              if (!rule.isEligible)
               errors += rule.description+"<br>"
            })
          }
        })

        return errors
      }

      return ''
    },
    hasValidCouponAppliedToBill(state) {
      const subOrders = get(state, 'bill.order.subOrderList', [])

      let hasValidCoupon = false
      subOrders.forEach(subOrder => {
        if (!hasValidCoupon) {
          const coupons = get(subOrder, 'coupons', [])

          const eligibleCouponIndex = coupons.findIndex(coupon =>
            get(coupon, 'ruleResult.isEligible', true) === true
          )

          hasValidCoupon = eligibleCouponIndex !== -1
        }
      })

      return hasValidCoupon
    },
    getServiceCharges(state) {
      return state.serviceCharges || []
    },
    getTableId(state) {
      return state.tableId
    },
    getTableDisplay(state) {
      return state.tableDisplay
    },
    getTableSetAt(state) {
      return state.tableSetAt
    },
    getEnteredViaQr(state) {
      return state.enteredViaQr
    },
    getLocalCart(state, getters, rootState, rootGetters)  {
      // Whatever store is currently set in store-details is where we fetch the cart from
      const storeId = rootGetters.getUniqueStoreId
      const cart = storeId
        ? state.carts[ storeId ] || []
        : state.localCart


      return cart
    },
    getServerCart(state) {
      return state.serverCart
    },
    getServerCartItems(state) {
      return get(state, 'serverCart.subOrderList[0].orderItemList', [])
    },
    hasOrder: (state, getters, _rootState, rootGetters) => () => {
      const activeOrder = getters.getActiveSubOrder()

      const serverOrderItems = activeOrder ? [
        ...get(activeOrder, 'orderItemList', []),
        ...get(activeOrder, 'punchCards', []),
        ...get(activeOrder, 'coupons', []).filter(option => option && option.orderItem),
        ...get(activeOrder, 'redemptionItems', []).filter(option => option && option.orderItem)
      ] : [];
      const activeCoupons = rootGetters.getActiveCoupon || [];
      const activeCouponOrderItems = activeCoupons.filter(coupon => coupon && coupon.orderItem);

      return !![...serverOrderItems, ...activeCouponOrderItems].length
    },
    getBill(state) {
      return state.bill
    },
    getBillTotal(state, getters) {
      return get(getters, 'getBill.totalAmount', 0) + getters.getTipsAmount + getters.getTipsForStoreAmount
    },
    // DEV-10377: Membership Balance used on this order -- the whole balance, up to the total,
    // unless the guest lowered it so a Stripe card keeps its $0.50 minimum. The backend honours
    // that cap only while the balance can't cover the order (PaymentAPI, DEV-11475), so it's
    // ignored here in exactly the same case: what the guest sees is what gets charged.
    getMembershipPayAmount(state, getters) {
      const balance = Math.max(0, Number(get(getters, 'getCustomerRewards.balance', 0)) || 0)
      const total = getters.getBillTotal
      const full = Math.min(balance, total)
      if (balance < total && state.membershipPayCap > 0) return Math.min(full, state.membershipPayCap)
      return full
    },
    // Sent with the payment only when the guest's choice actually lowers the amount.
    getMembershipPayAmountRequest(state, getters) {
      const balance = Math.max(0, Number(get(getters, 'getCustomerRewards.balance', 0)) || 0)
      const amount = getters.getMembershipPayAmount
      return amount < Math.min(balance, getters.getBillTotal) ? amount : null
    },
    getOrderType(state) {
      return state.orderType
    },
    getOrderTypeNeedsSet(state, getters) {
      if (state.orderType) {
        return false
      } else {
        if(getters.getStoreOrderTypes.length === 1) {
          return -1
        } else {
          return true
        }
      }
    },
    showTips(state, getters) {
      if (state.orderType == 'PICKUP' && getters.getStore.settings.orderSettings.allowPickupTips === false)
        return false
      return !getters.getPaymentType || !isSelectedMethod('offline', getters)
    },
    showMultipleTips(state, getters) {
      return getters.showTips && getters.getOrderType === 'DELIVERY'
              && get(getters.getStore, 'settings.deliverySettings.enableTipForStore', false) === true
              && get(getters.getBill, 'deliveryProvider') !== 'SELF'
    },
    getPaymentType(state) {
      // Kinda gross but provides backwards compatibility in state
      const types = state.paymentType || []
      return typeof types == 'object' ? types.filter(Boolean) : [types].filter(Boolean)
    },
    isDineIn(state) {
      return state.orderType === 'DINE_IN'
    },
    isDelivery(state) {
      return state.orderType === 'DELIVERY'
    },
    isPickUp(state) {
      return state.orderType === 'PICKUP'
    },
    isCartLoading(state) {
      return state.isCartLoading;
    },
    getNumCartItems: (state, getters, rootState, rootGetters) => {
      // In group order mode, count from participantCart (source of truth)
      const goState = rootState.groupOrder
      if (goState && goState.group && goState.participantCart) {
        var items = []
        if (goState.participantCart.subOrderList) {
          goState.participantCart.subOrderList.forEach(function(so) {
            if (so.orderItemList) items = items.concat(so.orderItemList)
          })
        }
        return items.reduce(function(acc, item) { return acc + (item.qty || 0) }, 0)
      }
      if (rootGetters['sharedCart/isActive']) {
        // Only the draft (unfired) items are still "in the cart" to check out —
        // items already SUBMITTED to the kitchen are a done deal and shouldn't
        // inflate the badge the guest uses to track what they're about to send.
        return (rootGetters['sharedCart/draftItems'] || []).reduce(function(acc, item) {
          return acc + (item.qty || 0)
        }, 0)
      }
      return [
        ...get(getters.getServerCart, 'subOrderList[0].orderItemList', []),
        ...get(getters.getServerCart, 'subOrderList[0].coupons', []),
        ...get(getters.getServerCart, 'subOrderList[0].redemptionItems', []),
        ...get(getters.getServerCart, 'subOrderList[0].punchCards', [])
      ].reduce((accumulator, object) => {
        return accumulator + (object.qty || 0);
      }, 0)
    },
    areItemsInCart(state, getters) {
      return getters.getNumCartItems === 0 ? false : true
    },
    getIsAnimating(state) {
      return state.isAnimating
    },
    getStoreOrderTypes(state, getters) {
      const storeFeatures = get(getters.getStore, 'features', {})

      return ORDER_TYPES.filter(type => storeFeatures[type.key] === true)
    },
    inFastServiceDineIn(state, getters) {
      // Fast service allows us to use dine in without needing table numbers
      return get(getters.getStore, 'settings.orderSettings.mode', 'DINE_IN') == 'FAST_SERVICE'
    },
    isDineInTableSet(state, getters) {
      return getters.getOrderType == 'DINE_IN' && state.tableId !== -1
    },
    getOrderTypeObject(state, getters) {
      const type = ORDER_TYPES.find(type => type.value === getters.getOrderType)

      return type
    },
    getDeliveryAddress(state) {
      return state.deliveryAddress
    },
    getDeliveryAddressResult(state) {
      return state.deliveryAddressResult
    },
    getDeliveryProvider(state) {
      return state.deliveryProvider
    },
    getDeliveryProvidersDetails(state) {
      return state.deliveryProvidersDetails
    },
    getStoreDeliveryProvider(state, getters) {
      return get(getters.getDeliveryProvider, `${getters.getStore.storeId}`)
    },
    getExistingOrder(state) {
      const activeMultiOrder = getActiveMultiOrder(state)
      if (activeMultiOrder && activeMultiOrder.subOrderList) {
        return [...activeMultiOrder.subOrderList]
      }
      return state.subOrder
    },
    isDineInAndExistingOrder(state, getters) {
      return state.orderType === "DINE_IN" && getters.getExistingOrder.length > 0
    },
    getSubOrderId(state) {
      const activeMultiOrder = getActiveMultiOrder(state)
      return activeMultiOrder ? activeMultiOrder.orderId : null
    },
    isInStore(state) {
      return state.inStore.inStore
    },
    isLocationDisabled(state) {
      return state.inStore.location
    },
    showInStoreDlg (state) {
      return state.inStore.inStoreDlg
    },


    getOriginalChargeFromCharges: (state, getters) => (charges) => {
      const _charges = charges.filter(charge => chargeDependenciesPass(charge, getters.getWorkingMenuItem)).sort((a, b) => compareTo(a, b))

      const nonPomotional = _charges.filter(charge => !charge.promotional && !charge.forMembership && !charge.schedule && !charge.forCombo)
      const nonPromotionalOrderType = nonPomotional.find(charge => charge.orderTypes && charge.orderTypes.includes(getters.getOrderType))

      // Prioritize charges specific to the current order type
      // If not, we want to search charges that aren't specific to another order type
      return nonPromotionalOrderType || nonPomotional.find(charge => !charge.orderTypes) || _charges.find(charge => !charge.orderTypes)
    },
    getFinalChargeFromCharges: (state, getters) => (charges = [], isCombo = false, isMember = getters.getCustomerRewards.isMember, order = getters.getServerCart) => {
      let isValidForOrderType = (charge, type) => isNullOrEmpty(charge.orderTypes) || charge.orderTypes.includes(type);
      let isValidForChannel = (charge, channel) => isNullOrEmpty(charge.channels) || charge.channels.includes(channel);
      const storeTimeZone = getters.getStore && (getters.getStore.timeZone || getters.getStore.timezone)
      const nowInStoreTime = storeTimeZone
        ? new Date(new Date().toLocaleString('en-US', { timeZone: storeTimeZone }))
        : new Date()
      let isValidSchedule = (schedule) => {
        const valid = !schedule || isNullOrEmpty(schedule.items) || isInSchedule(schedule.items, nowInStoreTime)
        return valid
      }
      if(!isCombo) {
        charges = charges.filter(charge => !charge.forCombo)
      }
      let compareTo = (o1, o2) => {
          let o1Priority = 0,
            o2Priority = 0;
          if (o1.forMembership) o1Priority += 100;
          if (o1.promotional) o1Priority += 10;
          if (o2.forMembership) o2Priority += 100;
          if (o2.promotional) o2Priority += 10;
          // A scheduled charge outranks an unscheduled fallback with equal other priority,
          // mirroring the backend's ChargeComparator (CatalogObjectCharge.java) which also
          // scores schedule presence -- otherwise an always-on $0 forCombo charge ties with,
          // and wins price-tiebreak over, a scheduled charge that's currently active.
          if (!isNullOrEmpty(o1.schedule)) o1Priority += 500;
          if (!isNullOrEmpty(o2.schedule)) o2Priority += 500;
          if (!isNullOrEmpty(o2.orderTypes)) o2Priority += 1000;
          if (!isNullOrEmpty(o1.orderTypes)) o1Priority += 1000;
          if (!isNullOrEmpty(o2.channels)) o2Priority += 10000;
          if (!isNullOrEmpty(o1.channels)) o1Priority += 10000;
          if (!isNullOrEmpty(o1.valueDependency)) o1Priority += 100000;
          if (!isNullOrEmpty(o2.valueDependency)) o2Priority += 100000;
          if(isCombo) {
            if (o1.forCombo) o1Priority += 100000;
            if (o2.forCombo) o2Priority += 100000;
          } else {
            if (o1.forCombo) o1Priority = 0;
            if (o2.forCombo) o2Priority = 0;
          }

          if (o2Priority != o1Priority) {
            return o2Priority - o1Priority;
          } else {
            if (o1.price < o2.price) return -1;
            else if (o1.price > o2.price) return 1;
            else return 0;
          }
      }

      if (isNullOrEmpty(charges))
        return null;

      let applicableCharges = [];

      // Collect all applicable charges
      charges.forEach(charge => {
        if ((isMember || !charge.forMembership)
          && isValidForOrderType(charge, order.orderType)
          && isValidForChannel(charge, 'ONLINE')
          && chargeDependenciesPass(charge, getters.getWorkingMenuItem)
          && isValidSchedule(charge.schedule)
        )
          applicableCharges.push(charge);
      });

      if (applicableCharges.length > 0) {
        applicableCharges.sort((a, b) => compareTo(a, b));
        return applicableCharges[0];
      } else {
        return null;
      }
    },

    sumLocalMenuItem: (state, getters, rootState, rootGetters) => (menuItem) => {
      if ('subTotal' in menuItem)
        return menuItem.subTotal

      const subOrders = get(state, 'serverCart.subOrderList', [])

      const { charges = [], itemAttributeList = [], qty = 1 } = menuItem || {}

      let sums = []

      const charge = getters.getFinalChargeFromCharges(charges)
      if(charge) {
        sums.push(charge.price)
      }

      itemAttributeList.forEach(option => {
        // All attributes in option need to be summed
        const { attrValueList = [] } = option

        // For each attribute of the option we must sum
        attrValueList.forEach(attr => {
          const attrQty = isNumber(attr.qty) ? attr.qty : 1
          const attrCharge = getters.getFinalChargeFromCharges(attr.charges)
          if(attrCharge) {
            sums.push(attrCharge.price * attrQty)
          }
        })
      })

      sums = sums.map(s => s * qty)

      const subtotal = sums.reduce((acc, curr) => acc + curr, 0)

      // EPSILON constant to avoid rounding errors such as 1.005
      return Math.round((subtotal + Number.EPSILON) * 100) / 100
    },

   getPunchCards(state, getters) {
    if (getters.doesStoreSupportFeature('rewardsPunchCardSupported')) {
      const rewardsPunchCards = get(getters.getRewards, 'punchCards', []);
      const userPunchCards = get(getters.getCustomerRewards, 'punchCards', []);
      const rewardMap = {};
      rewardsPunchCards.forEach(rpc => {
        if (rpc.code) rewardMap[rpc.code] = rpc;
      });

      userPunchCards.forEach(pc => {
        if (rewardMap[pc.code] && rewardMap[pc.code].validTo) {
          pc.validTo = rewardMap[pc.code].validTo;
        }
      });

      const validUserPunchCards = userPunchCards.filter(pc => rewardMap[pc.code]);

      const storePunchCards = rewardsPunchCards.filter(pc =>
        (!pc.storeId || pc.storeId == getters.getStore.storeId) && pc.enabled
      );

      const allPunchCards = [...validUserPunchCards, ...storePunchCards];
      const uniquePunchCards = allPunchCards.filter(
        (pc, idx, arr) => arr.findIndex(p => p.code === pc.code) === idx
      );

      return uniquePunchCards;
    }
    return [];
  },
    getServerPunchCards(state, getters) {
      if (getters.doesStoreSupportFeature('rewardsPunchCardSupported'))
        return get(getters, 'getServerCart.subOrderList', [])
                .map(item => item.punchCards)
                .flat()
                .filter(punchCard => !!punchCard)
    },
    getActivePunchCards: (state, getters) => () => {
      const feature = getters.doesStoreSupportFeature('rewardsPunchCardSupported')
      const punchCards = getters.getPunchCards
      const currentStoreId = getters.getStore.storeId

      const determineQty = (punchCard) => {
        if (punchCard.using)
          return punchCard.using
        return 0
      }

      if (!feature || !punchCards)
        return []

      return punchCards.map(punchCard => {
        return {
          code: punchCard.code,
          qty: determineQty(punchCard),
          storeId: punchCard.storeId
        }
      }).filter(punchCard => punchCard.storeId == currentStoreId && punchCard && punchCard.qty)
    },

    getCoupons(state, getters) {
      let coupons = [...getters.getActiveCoupon]

      coupons.forEach(coupon => {
        delete coupon.compositeList
      });

      return coupons
    },

    getRewards: (state) => state.rewards,
    getConfigRewards: (state) => state.rewardsConfig,
    getActiveRewards: (state) => state.activeRewards,
    getPromotions: (state) => state.promotions,

    // Returns the best promotion for an item (highest discount wins)
    getBestPromotionForItem: (state) => (itemCode, itemTags, basePrice) => {
      if (basePrice <= 0) return null

      const matchingPromos = []

      // Check direct item code match
      const itemPromos = state.promotionsByItemCode[itemCode] || []
      itemPromos.forEach(promo => {
        const actualDiscount = promo.percentageBased ? basePrice * promo.discountValue : promo.discountValue
        matchingPromos.push({ ...promo, actualDiscountCents: actualDiscount })
      })

      // Check tag matches
      ;(itemTags || []).forEach(tag => {
        const tagCode = typeof tag === 'string' ? tag : (tag && tag.code) || ''
        const tagPromos = state.promotionsByTag[tagCode] || []
        tagPromos.forEach(promo => {
          const actualDiscount = promo.percentageBased ? basePrice * promo.discountValue : promo.discountValue
          matchingPromos.push({ ...promo, actualDiscountCents: actualDiscount })
        })
      })

      // Check category matches (itemCode contains "categories_XXX")
      Object.keys(state.promotionsByCategory).forEach(catKey => {
        if (itemCode.includes(catKey)) {
          const catPromos = state.promotionsByCategory[catKey] || []
          catPromos.forEach(promo => {
            const actualDiscount = promo.percentageBased ? basePrice * promo.discountValue : promo.discountValue
            matchingPromos.push({ ...promo, actualDiscountCents: actualDiscount })
          })
        }
      })

      if (matchingPromos.length === 0) return null

      // Return highest discount
      matchingPromos.sort((a, b) => b.actualDiscountCents - a.actualDiscountCents)
      return matchingPromos[0]
    },

    getActiveSubOrder: (state, getters) => () => {
      return get(getters.getServerCart, 'subOrderList', []).find(order => !(`seq` in order))
      || get(getters.getServerCart, 'subOrderList', []).reduce((prev, curr) => {
        return (prev && prev.seq > curr.seq) ? prev : curr
      }, null)
    }
  },

  actions: {
    mergeServerWithLocalCart({ commit, getters, rootGetters }) {
      const localCart = getters.getLocalCart || []
      const serverCart = getters.getServerCart
      const { subOrderList = [] } = serverCart || {}

      // Need to handle dine in eventually, for now, assume the highest sequence is the working order
      const order = subOrderList.reduce((prev, curr) => {
        return (prev && prev.seq > curr.seq) ? prev : curr
      }, null)


      if (order) {
        const { orderItemList = [] } = order

        // Create map for easy lookup
        const localOrderItemMap = localCart.reduce((prev, curr) => {
          return { ...prev, [curr.menuItem.uuid]: curr.menuItem}
        }, {})

        const grabPrices = (localItem, serverItem) => {
          const testKey = (key) => {
            if (`${key}` in serverItem)
              localItem[key] = serverItem[key]
          }

          const keys = [
            'uuid',
            'qty',
            'price',
            'itemPrice',
            'subItemPrice',
            'subTotal',
            'relationPrice',
            'discountTotal',
            'errors'
          ].forEach(key => {
            testKey(key)
          });
        }

        const mergeAttributes = (item, serverItem) => {
          if (!item || !serverItem || !item.itemAttributeList || !serverItem.itemAttributeList) return

          item.itemAttributeList.sort((a, b) => {
            const indexA = serverItem.itemAttributeList.findIndex(_item => _item.code == a.code)
            const indexB = serverItem.itemAttributeList.findIndex(_item => _item.code == b.code)
            return indexA < indexB ? -1 : 1
          })

          get(item, 'itemAttributeList', []).forEach((attribute, attrIndex) => {
            const modCode = get(item, `itemAttributeList[${attrIndex}].code`, null)
            const serverAttribute = serverItem.itemAttributeList.find((_item) => _item.code == modCode)

            attribute.attrValueList.sort((a, b) => {
              const indexA = serverAttribute.attrValueList.findIndex(_item => _item.code == a.code)
              const indexB = serverAttribute.attrValueList.findIndex(_item => _item.code == b.code)
              return indexA < indexB ? -1 : 1
            })

            get(attribute, 'attrValueList', []).forEach((option, optionIndex) => {
              // Server gives name. Front end uses value
              const valueCode = get(item, `itemAttributeList[${attrIndex}].attrValueList[${optionIndex}].code`, null)
              const childName = get(item, `itemAttributeList[${attrIndex}].attrValueList[${optionIndex}].value`, null)

              if (!!serverAttribute) {
                const serverValue = serverAttribute.attrValueList.find((_item) => _item.code == valueCode)

                if (!!serverValue)
                  item.itemAttributeList[attrIndex].attrValueList[optionIndex].value = serverValue.name
              }
            })
          })
        }

        const updatedLocalCart = orderItemList.map((serverItem, index) => {
          const localItem = localOrderItemMap[serverItem.uuid] || get(localCart, `[${index}].menuItem`, {})

          // We want to prioritize the local menu serverItem mostly but force the server cart values in certain places
          let menuItem = serverItem.code == localItem.code ? {
            ...serverItem, 
            ...localItem,
            childItemList: serverItem.childItemList || localItem.childItemList
          } : serverItem

          menuItem.uuid = serverItem.uuid
          menuItem.name = serverItem.name

          if (!!serverItem)
            grabPrices(menuItem, serverItem)

          get(localItem, 'childItemList', []).forEach((relation, childIndex) => {
            const serverRelation = get(serverItem, `childItemList[${childIndex}]`)
            if (serverRelation) {
              relation.name = serverRelation.name || relation.name
              grabPrices(relation, serverRelation)
            }

            get(relation, 'childItemList', []).forEach((child, i) => {
              const serverChild = get(serverRelation, `childItemList[${i}]`)
              if (serverChild) {
                child.name = serverChild.name || child.name
                // Pull the newly calucated prices from the backend
                grabPrices(child, serverChild)
              }
              mergeAttributes(child, serverChild)
            });
          });

          mergeAttributes(menuItem, serverItem)

          return { menuItem }
        })

        const storeId = rootGetters.getUniqueStoreId
        const payload = {
          storeId,
          cart: updatedLocalCart
        }

        commit('SET_LOCAL_CART', payload)
      }
    },
    setCouponCode({ commit, dispatch, rootGetters }, data) {
      const { couponCode } = data || {}

      const payload = {
        storeId: rootGetters.getUniqueStoreId,
        couponCode
      }

      commit('SET_COUPON_CODE', payload)
    },
    validateDeliveryAddress({}, address){
      return this.$api.post('/order/validate_providers', address)
    },
    setTableId({ commit, dispatch, rootState }, tableId) {
      const store = Object.assign({}, rootState.storeDetail.store)
      const payload = { tableId, store }
      commit('SET_TABLE_ID', payload)

      if (tableId && tableId !== -1) {
        dispatch('fetchShoppingCart')
      }
    },
    // DEV-11506: a table URL (?tid= / ?dineInOnly= -- what every dine-in QR carries) is
    // always a DINE_IN order. Re-asserts that wherever order type can drift (shared-cart
    // session end, submit). Same truthy check Cart.vue/StoreLocationDetails.vue use to hide
    // the order-type selector on these routes. Commits only -- no refetch -- so callers stay
    // in control of side effects; table number is left alone (checkTableNumExpired's
    // re-scan still applies). Resolves true when the current route is a table route.
    enforceTableOrderType({ state, commit }) {
      const query = get(this, '$router.currentRoute.query', {}) || {}
      if (!query.tid && !query.dineInOnly) return false
      if (state.orderType !== 'DINE_IN') commit('SET_ORDER_TYPE', 'DINE_IN')
      return true
    },
    setPaymentType({ commit, getters }, paymentType) {
      const primaryType = get(getters, 'getPaymentType[0]')

      // If membership is the primary payment method but the user does not have sufficient points,
      // Allow a second choice to make up the difference
      // offline payment is the only method that cannot be mixed with membership
      if (primaryType == 'membership' && paymentType != 'offline' && (getters.getCustomerRewards.balance < getters.getBillTotal)) {
        if (paymentType == 'membership') {
          // If membership was selected again, allow it to be unselected maintaining the secondary if possible
          commit('SET_PAYMENT_TYPE', getters.getPaymentType.length > 1 ? [ getters.getPaymentType[1] ] : [])
        } else if (!getters.getPaymentType[1] || getters.getPaymentType[1] != paymentType || paymentType == 'online') {
          // Allow for secondary payment methods. Clear if it is already secondary though
          // Online is only unselected if you select the same card, which is handled by checkoutForm.vue since we don't have that info
          commit('SET_PAYMENT_TYPE', [ 'membership', paymentType ])
        } else {
          commit('SET_PAYMENT_TYPE', [ 'membership' ])
        }
      } else {
        // Online is the only option that has sub options. So if we change credit cards it can still be set to online
        commit('SET_PAYMENT_TYPE', paymentType && (paymentType != primaryType || paymentType == 'online') ? [ paymentType ] : [])
      }
    },
    async addToCart({ commit, dispatch, rootGetters }, menuItem) {
      // Group order mode: send to group order API only, skip local cart entirely
      if (rootGetters['groupOrder/isInGroupOrder'] && !rootGetters['groupOrder/myCartLocked']) {
        const goItem = {
          code: menuItem.code || '',
          name: menuItem.name || menuItem.nameTranslation || '',
          price: menuItem.itemPrice || menuItem.price || 0,
          qty: menuItem.qty || 1,
          itemAttributeList: menuItem.itemAttributeList || [],
          childItemList: menuItem.childItemList || [],
          relations: menuItem.relations || [],
          specialRequest: menuItem.specialRequest || '',
        }
        // Merge with existing line item if it's a plain item (no modifiers/children/special request)
        const isConfigurable = goItem.itemAttributeList.length > 0 ||
          (goItem.childItemList && goItem.childItemList.length > 0) ||
          !!menuItem.specialRequest
        if (!isConfigurable) {
          const existing = (rootGetters['groupOrder/myCartItems'] || []).find(i => i.code === goItem.code)
          if (existing && existing.uuid) {
            try {
              await dispatch('groupOrder/mutateCart', {
                operations: [{ type: 'EDIT', itemUuid: existing.uuid, item: Object.assign({}, existing, { qty: (existing.qty || 1) + (goItem.qty || 1) }) }],
              }, { root: true })
            } catch (e) {
              console.error('[GroupOrder] mutateCart EDIT (merge) failed:', e)
            }
            return
          }
        }
        try {
          // specialRequest is set directly on the item (goItem.specialRequest above) --
          // OrderItem has its own native per-item specialRequest field on the backend,
          // so there's no need to pack it into the sub-order-level concatenated string.
          await dispatch('groupOrder/mutateCart', {
            operations: [{ type: 'ADD', item: goItem }],
          }, { root: true })
        } catch (e) {
          console.error('[GroupOrder] mutateCart ADD failed:', e)
        }
        return
      }

      if (rootGetters['sharedCart/isActive']) {
        const sharedItem = buildSharedCartItem(menuItem)
        const isConfigurable = sharedItem.itemAttributeList.length > 0 ||
          (sharedItem.childItemList && sharedItem.childItemList.length > 0) ||
          !!sharedItem.specialRequest
        if (!isConfigurable) {
          // Only merge into an item still in the draft round — an item already
          // fired to the kitchen (SUBMITTED) is locked and re-versioned by the
          // backend, so editing it by uuid would fail. Adding again should
          // create a new draft line instead.
          const existing = (rootGetters['sharedCart/draftItems'] || []).find(i => i.code === sharedItem.code)
          if (existing && existing.uuid) {
            try {
              await dispatch('sharedCart/mutateCart', {
                operations: [{ type: 'EDIT', itemUuid: existing.uuid, expectedItemVersion: existing.version, item: Object.assign({}, existing, { qty: (existing.qty || 1) + (sharedItem.qty || 1) }) }]
              }, { root: true })
            } catch (e) {
              console.error('[SharedCart] mutateCart EDIT (merge) failed:', e)
            }
            return
          }
        }
        try {
          await dispatch('sharedCart/mutateCart', {
            operations: [{ type: 'ADD', item: sharedItem }]
          }, { root: true })
        } catch (e) {
          console.error('[SharedCart] mutateCart ADD failed:', e)
        }
        return
      }

      // Regular order mode — unchanged
      const storeId = rootGetters.getUniqueStoreId
      let menuItemCopy = menuItem
      const isQuantityBasedPromotion = menuItemCopy.relations && menuItemCopy.relations.length > 0 &&
        menuItemCopy.relations.some(rel => rel.charge && rel.charge.quantityRule);
      if (menuItemCopy.relations && menuItemCopy.relations.length) {
        menuItemCopy.relations.forEach((item) => {
          if (item && item.selectedItem && item.selectedItem.length) {
            const newSelectedItemList = combineAndRemoveDuplicates(item.selectedItem);
            item.selectedItem = newSelectedItemList;
          }
        });
        menuItemCopy.childItemList.forEach((item) => {
          if (item && item.childItemList && item.childItemList.length) {
            const newChildItemList = combineAndRemoveDuplicates(item.childItemList);
            item.childItemList = newChildItemList;
          }
        });
      }
      try {
        const enName = (menuItemCopy.translations && menuItemCopy.translations.find(t => t.lang === 'en'))
          ? menuItemCopy.translations.find(t => t.lang === 'en').value
          : (menuItemCopy.name || '');
        const itemPrice = Number(menuItemCopy.price || (Array.isArray(menuItemCopy.charges) && menuItemCopy.charges[0] && menuItemCopy.charges[0].price) || 0);
        const itemQty = Number(menuItemCopy.qty || 1);
        const storeId = String((rootGetters.getStore && rootGetters.getStore.storeId) || '')
        const orderType = rootGetters.getOrderType || ''
        const userId = rootGetters.getUserID ? String(rootGetters.getUserID) : undefined
        const addToCartPayload = {
          event: 'add_to_cart',
          ecommerce: {
            items: [{ item_id: String(menuItemCopy.code || ''), item_name: enName, price: itemPrice, quantity: itemQty }],
            store_id: storeId,
            order_type: orderType,
            ...(userId && { user_id: userId }),
          },
        };
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(addToCartPayload);
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'add_to_cart', addToCartPayload.ecommerce);
        }
        if (typeof window.fbq === 'function') {
          const currency = get(rootGetters, 'getStore.currency', 'CAD')
          window.fbq('track', 'AddToCart', {
            content_name: enName,
            content_ids: [String(menuItemCopy.code || '')],
            content_type: 'product',
            value: itemPrice * itemQty,
            currency,
            contents: [{ id: String(menuItemCopy.code || ''), quantity: itemQty }],
          });
        }
      } catch (e) { console.warn('add_to_cart tracking error', e) }

      const payload = { storeId, menuItem: filterUserInput(menuItemCopy) };
      commit('CART_ADD_ITEM', payload);
      if (isQuantityBasedPromotion) {
        return new Promise(resolve => {
          setTimeout(() => {
            Promise.all([
              dispatch('fetchOptions'),
              rootGetters.getMenu && rootGetters.getMenu.code ?
                dispatch('fetchAllMenus', rootGetters.getMenu.code) : Promise.resolve()
            ]).then(() => {
              return dispatch('fetchShoppingCart').then(resolve);
            }).catch(resolve);
          }, 50);
        });
      } else {
        return dispatch('fetchShoppingCart');
      }
    },
    addManyToCart({ commit, dispatch, rootGetters }, menuItems) {
      const storeId = rootGetters.getUniqueStoreId
      menuItems.map(menuItem => {
        commit("CART_ADD_ITEM", {
          storeId,
          menuItem: filterUserInput(menuItem)
        });
        }
      );
      dispatch("fetchShoppingCart");
    },
    // DEV-11529: GA4 (dataLayer/gtag) + Meta cart-funnel events so stores can measure abandonment.
    // Regular cart only; one cartItem (with quantity) or else the whole cart. user_id is the
    // anonymous Snappy customer id when logged in -- never phone/email, no PII in browser tags.
    trackCartEvent({ getters, rootGetters }, { event, cartItem, quantity, extra }) {
      if (rootGetters['groupOrder/isInGroupOrder'] || rootGetters['sharedCart/isActive']) return
      try {
        const items = cartItem
          ? [{ ...toGa4Item(getters, cartItem), quantity }]
          : (getters.getLocalCart || []).map(item => toGa4Item(getters, item))
        if (!items.length) return
        const currency = get(rootGetters, 'getStore.currency', 'CAD')
        const value = items.reduce((sum, i) => sum + i.price * i.quantity, 0)
        const userId = rootGetters.getUserID ? String(rootGetters.getUserID) : undefined
        const ecommerce = {
          currency,
          value,
          items,
          store_id: String(get(rootGetters, 'getStore.storeId', '')),
          order_type: rootGetters.getOrderType || '',
          ...(userId && { user_id: userId }),
          ...extra
        }
        window.dataLayer = window.dataLayer || []
        // GTM merges ecommerce pushes (arrays too): clear before and after so this event and the
        // older un-cleared pushes (view_item, add_to_cart, purchase) don't pick up each other's items
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({ event, ecommerce })
        window.dataLayer.push({ ecommerce: null })
        if (typeof window.gtag === 'function') window.gtag('event', event, ecommerce)
        const fbEvent = { add_to_cart: 'AddToCart', begin_checkout: 'InitiateCheckout', add_payment_info: 'AddPaymentInfo' }[event]
        if (fbEvent && typeof window.fbq === 'function') {
          window.fbq('track', fbEvent, {
            content_ids: items.map(i => i.item_id),
            content_type: 'product',
            contents: items.map(i => ({ id: i.item_id, quantity: i.quantity })),
            num_items: items.reduce((n, i) => n + i.quantity, 0),
            value,
            currency
          })
        }
      } catch (e) {
        console.warn(`${event} tracking error`, e)
      }
    },
    async incrementCart({commit, dispatch, getters, rootGetters}, index) {
      const storeId = rootGetters.getUniqueStoreId

      if (rootGetters['groupOrder/isInGroupOrder'] && !rootGetters['groupOrder/myCartLocked']) {
        const goItems = rootGetters['groupOrder/myCartItems'] || []
        const goItem = goItems[index]
        if (goItem && goItem.uuid) {
          // Spending limit enforcement (host-exempt, discount-aware) happens
          // server-side -- mutateCart's error handler already surfaces
          // GROUP_ORDER_CART_LIMIT_EXCEEDED via the same snackbar. A client-side
          // pre-check here duplicated that with worse logic: it used the raw item
          // price (ignoring discounts/promos) and never exempted the host.
          //
          // SET_CART_LOADING (same as the sharedCart branch below) is what
          // CartMenuItemDisplay's handleIncrement/handleDecrement actually check
          // (cartIsLoading) before allowing another click -- without it, mashing +/-
          // fires overlapping mutateCart calls that each read expectedCartVersion at
          // click time, so a second click can send a version already stale by the time
          // it reaches the backend (GROUP_ORDER_CART_VERSION_CONFLICT).
          commit('SET_CART_LOADING', true)
          try {
            await dispatch('groupOrder/mutateCart', {
              operations: [{ type: 'EDIT', itemUuid: goItem.uuid, item: Object.assign({}, goItem, { qty: (goItem.qty || 1) + 1 }) }],
            }, { root: true })
            // UI updates reactively from groupOrder/myCartItems — no local cart mirror needed
          } catch (e) {
            console.error('[GroupOrder] mutateCart EDIT (increment) failed:', e)
          } finally {
            commit('SET_CART_LOADING', false)
          }
        }
        return
      }

      if (rootGetters['sharedCart/isActive']) {
        // draftItems, not items -- items also includes already-fired rounds, whose
        // objects are stale snapshots (their version never advances after firing).
        // Indexing/mutating against those sends a stale expectedItemVersion and the
        // backend correctly rejects it -- and once any round has fired, `index` (which
        // the UI derives from the draft-only list it renders as editable) would land on
        // the wrong item entirely in the combined fired+draft array.
        const sharedItems = rootGetters['sharedCart/draftItems'] || []
        const sharedItem = sharedItems[index]
        if (sharedItem && sharedItem.uuid) {
          commit('SET_CART_LOADING', true)
          try {
            await dispatch('sharedCart/mutateCart', {
              operations: [{ type: 'EDIT', itemUuid: sharedItem.uuid, expectedItemVersion: sharedItem.version, item: Object.assign({}, sharedItem, { qty: (sharedItem.qty || 1) + 1 }) }]
            }, { root: true })
          } catch (e) {
            console.error('[SharedCart] mutateCart EDIT (increment) failed:', e)
          } finally {
            commit('SET_CART_LOADING', false)
          }
        }
        return
      }

      const payload = { index, storeId }
      const cartItem = (getters.getLocalCart || [])[index]
      commit('CART_INCREMENT_ITEM', payload)
      if (cartItem) dispatch('trackCartEvent', { event: 'add_to_cart', cartItem, quantity: 1 })
      dispatch('fetchShoppingCart')
    },
    async decrementCart({commit, dispatch, getters, rootGetters}, index) {
      const storeId = rootGetters.getUniqueStoreId

      if (rootGetters['groupOrder/isInGroupOrder'] && !rootGetters['groupOrder/myCartLocked']) {
        const goItems = rootGetters['groupOrder/myCartItems'] || []
        const goItem = goItems[index]
        if (goItem && goItem.uuid) {
          const currentQty = goItem.qty || 1
          const operation = currentQty > 1
            ? { type: 'EDIT', itemUuid: goItem.uuid, item: Object.assign({}, goItem, { qty: currentQty - 1 }) }
            : { type: 'REMOVE', itemUuid: goItem.uuid }
          // Same SET_CART_LOADING reasoning as incrementCart above.
          commit('SET_CART_LOADING', true)
          try {
            await dispatch('groupOrder/mutateCart', { operations: [operation] }, { root: true })
            // UI updates reactively from groupOrder/myCartItems — no local cart mirror needed
          } catch (e) {
            console.error('[GroupOrder] mutateCart decrement failed:', e)
          } finally {
            commit('SET_CART_LOADING', false)
          }
        }
        return
      }

      if (rootGetters['sharedCart/isActive']) {
        // draftItems -- see incrementCart's comment above for why.
        const sharedItems = rootGetters['sharedCart/draftItems'] || []
        const sharedItem = sharedItems[index]
        if (sharedItem && sharedItem.uuid) {
          const currentQty = sharedItem.qty || 1
          const operation = currentQty > 1
            ? { type: 'EDIT', itemUuid: sharedItem.uuid, expectedItemVersion: sharedItem.version, item: Object.assign({}, sharedItem, { qty: currentQty - 1 }) }
            : { type: 'REMOVE', itemUuid: sharedItem.uuid, expectedItemVersion: sharedItem.version }
          commit('SET_CART_LOADING', true)
          try {
            await dispatch('sharedCart/mutateCart', { operations: [operation] }, { root: true })
          } catch (e) {
            console.error('[SharedCart] mutateCart decrement failed:', e)
          } finally {
            commit('SET_CART_LOADING', false)
          }
        }
        return
      }

      const payload = { index, storeId }
      const cartItem = (getters.getLocalCart || [])[index]
      const lineQty = cartItem && cartItem.menuItem.qty
      commit('CART_DECREMENT_ITEM', payload)
      // Line gone (qty 1 or at minCardinality) -> its whole qty left the cart, otherwise one
      if (cartItem) dispatch('trackCartEvent', { event: 'remove_from_cart', cartItem, quantity: getters.getLocalCart[index] === cartItem ? 1 : lineQty })
      dispatch('fetchShoppingCart')
    },
    async removeFromCart({ commit, dispatch, rootGetters }, menuItem) {
      const storeId = rootGetters.getUniqueStoreId

      // Group order mode: remove via API only, UI updates from participantCart
      if (rootGetters['groupOrder/isInGroupOrder'] && !rootGetters['groupOrder/myCartLocked']) {
        const itemUuid = (menuItem && menuItem.menuItem && menuItem.menuItem.uuid)
          || (menuItem && menuItem.uuid)
        if (itemUuid) {
          // Same SET_CART_LOADING reasoning as incrementCart above.
          commit('SET_CART_LOADING', true)
          try {
            await dispatch('groupOrder/mutateCart', {
              operations: [{ type: 'REMOVE', itemUuid: itemUuid }],
            }, { root: true })
          } catch (e) {
            console.error('[GroupOrder] mutateCart REMOVE failed:', e)
          } finally {
            commit('SET_CART_LOADING', false)
          }
        }
        return
      }

      if (rootGetters['sharedCart/isActive']) {
        const itemUuid = (menuItem && menuItem.menuItem && menuItem.menuItem.uuid)
          || (menuItem && menuItem.uuid)
        if (itemUuid) {
          // draftItems -- see incrementCart's comment above for why.
          const sharedItem = (rootGetters['sharedCart/draftItems'] || []).find(i => i.uuid === itemUuid)
          commit('SET_CART_LOADING', true)
          try {
            await dispatch('sharedCart/mutateCart', {
              operations: [{ type: 'REMOVE', itemUuid: itemUuid, expectedItemVersion: sharedItem && sharedItem.version }]
            }, { root: true })
          } catch (e) {
            console.error('[SharedCart] mutateCart REMOVE failed:', e)
          } finally {
            commit('SET_CART_LOADING', false)
          }
        }
        return
      }

      const payload = { menuItem, storeId }
      commit('CART_REMOVE_ITEM', payload);
      dispatch('fetchShoppingCart')
    },
    async replaceCartItem({commit, dispatch, rootGetters}, menuItem) {
      const storeId = rootGetters.getUniqueStoreId
      const index = rootGetters.getCartItemIndex
      let menuItemCopy = menuItem;
      if (menuItemCopy.relations && menuItemCopy.relations.length) {
        menuItemCopy.relations.forEach((item) => {
          if (item && item.selectedItem && item.selectedItem.length) {
            const newSelectedItemList = combineAndRemoveDuplicates(
              item.selectedItem
            );
            item.selectedItem = newSelectedItemList;
          }
        });
        menuItemCopy.childItemList.forEach((item) => {
          if (item && item.childItemList && item.childItemList.length) {
            const newChildItemList = combineAndRemoveDuplicates(
              item.childItemList
            );
            item.childItemList = newChildItemList;
          }
        });
      }
      const payload = {
        storeId,
        menuItem: filterUserInput(menuItemCopy),
        index, storeId
      };
      commit('CART_REPLACE_ITEM', payload)

      if (rootGetters['groupOrder/isInGroupOrder'] && !rootGetters['groupOrder/myCartLocked']) {
        const existingItems = rootGetters['groupOrder/myCartItems'] || []
        const existing = existingItems[index] || existingItems.find(i => i.uuid === menuItemCopy.uuid)
        if (existing && existing.uuid) {
          const updatedItem = {
            code: menuItemCopy.code || existing.code || '',
            name: menuItemCopy.name || menuItemCopy.nameTranslation || existing.name || '',
            price: menuItemCopy.itemPrice || menuItemCopy.price || existing.price || 0,
            qty: menuItemCopy.qty || existing.qty || 1,
            specialRequest: menuItemCopy.specialRequest || '',
            itemAttributeList: menuItemCopy.itemAttributeList || [],
            childItemList: menuItemCopy.childItemList || [],
            relations: menuItemCopy.relations || [],
          }
          try {
            // specialRequest is set directly on updatedItem above -- no need to also send
            // the concatenated sub-order-level string (see addToCart's ADD path for why).
            await dispatch('groupOrder/mutateCart', {
              operations: [{ type: 'EDIT', itemUuid: existing.uuid, item: updatedItem }],
            }, { root: true })
          } catch (e) {
            console.error('[GroupOrder] mutateCart EDIT (replace) failed:', e)
          }
          return
        }
      }

      if (rootGetters['sharedCart/isActive']) {
        // draftItems -- see incrementCart's comment above for why.
        const sharedItems = rootGetters['sharedCart/draftItems'] || []
        const existing = sharedItems[index]
        if (existing && existing.uuid) {
          try {
            await dispatch('sharedCart/mutateCart', {
              operations: [{ type: 'EDIT', itemUuid: existing.uuid, expectedItemVersion: existing.version, item: Object.assign({}, existing, buildSharedCartItem(menuItemCopy)) }]
            }, { root: true })
          } catch (e) {
            console.error('[SharedCart] mutateCart EDIT (replace) failed:', e)
          }
        }
        return
      }

      dispatch('fetchShoppingCart')
    },
    setOrderType({ commit, getters, dispatch }, orderType) {
      const orderTypeChanged = getters.getOrderType !== orderType
      if (orderType !== 'DELIVERY') {
        const storeId = getters.getUniqueStoreId
        // Check typed coupon code (CouponInput system)
        const appliedCode = getters.getCouponCode
        const promotions = getters.getPromotions || []
        const appliedPromo = promotions.find(p => p.code === appliedCode)
        if (appliedPromo && appliedPromo.freeDelivery) {
          commit('SET_COUPON_CODE', { storeId, couponCode: null })
          dispatch('showSnack', { text: 'This coupon is only valid for delivery orders', color: 'error' })
        }
        // Check carousel-selected coupon (activeCoupon system)
        const activeCoupons = getters.getActiveCoupon || []
        const activeFreeDelivery = activeCoupons.find(c => c && c.freeDelivery)
        if (activeFreeDelivery) {
          commit('SET_ACTIVE_COUPON', null)
          dispatch('showSnack', { text: 'This coupon is only valid for delivery orders', color: 'error' })
        }
      }

      commit('SET_ORDER_TYPE', orderType)

      // When we set the order type, we always need to refresh the cart because the price
      // can be impacted by a delivery charge
      const shouldFetchCart = orderType !== 'DINE_IN' || (orderType === 'DINE_IN' && getters.getTableId !== -1);
      if (shouldFetchCart) {
        dispatch('fetchShoppingCart')
      }

      // The live menu also needs to be refreshed, not just the cart and promos.
      // Menu item charges and sales-type-specific pricing are loaded with the current
      // order type; otherwise the card keeps showing the stale delivery "From" price
      // and promo tag after a switch without a reload.
      const currentMenuCode = getters.getMenu && getters.getMenu.code
      if (currentMenuCode) {
        dispatch('fetchAllMenus', currentMenuCode)
      }

      // Promotions are filtered server-side by order type (fetchPromotions calls
      // /promotions?orderType=...), so a delivery-only promo (e.g. BOGO) otherwise
      // stays in state.promotions -- and its badge stays on menu cards -- after
      // switching to pickup, since nothing previously re-fetched on this change.
      dispatch('fetchPromotions')

      // AI recommendations/popular-items are order-type-specific (e.g. delivery-only
      // items shouldn't show for pickup) -- re-fetch so the row reflects the new type.
      // Cleared first so the old type's picks never show while the new ones load.
      if (orderTypeChanged) {
        commit('SET_AI_RECOMMENDATIONS', [])
        dispatch('fetchAiRecommendations')
      }
    },
    checkTableNumExpired({ commit, getters, dispatch }, showSnackMessage=false) {
      // Table number must be entered every 2.5 hours
      if (getters.getTableSetAt > 0 && (new Date().getTime() - getters.getTableSetAt) / 60000 > 150) {
        commit('CLEAR_TABLE_ID')

        // We don't need this message if the user just completed an order
        if (showSnackMessage && getters.getOrderType == 'DINE_IN')
          dispatch('showSnack', { text: 'Please re-select your table' })
      }
    },
    resetCart({ commit, getters, dispatch, rootGetters }) {
      const storeId = rootGetters.getUniqueStoreId
      const tempSlots = getters.getValidTimeSlots

      // Merchant changes must also discard the separate Group Order session.
      dispatch('groupOrder/clearGroupOrder', null, { root: true })
      dispatch('clearCheckoutInfo')
      commit('RESET_CART', { storeId, tempSlots })
      commit('SET_ACTIVE_COUPON', null)
      commit('SET_ACTIVE_REWARDS', null)
      dispatch('sharedCart/clearSharedCart', null, { root: true })

      dispatch('checkTableNumExpired')
    },
    setDeliveryAddress({ commit, dispatch }, data) {
      const { address, result } = data || {}
      commit('SET_DELIVERY_ADDRESS', address)
      commit('SET_DELIVERY_ADDRESS_RESULT', result)

      dispatch('fetchShoppingCart')
    },
    // Single source of truth for "is the user's currently-selected scheduled time still
    // orderable" -- previously duplicated separately in Cart.vue and CheckoutPage.vue, and
    // driven by a passive setInterval(..., 60000) that fired regardless of what the user
    // was doing (browsing order history, idle, anywhere), popping the dialog back open on
    // its own schedule instead of the user's. Per DEV-10876: check ONLY at the moments the
    // user is actually about to act on the schedule -- add/edit an item, click a checkout
    // button, or submit/pay -- as an early-return guard before that action proceeds. No
    // interval anywhere calls this anymore; every caller is a real user-intent trigger.
    //
    // { throttle: true } (add/edit-item only) skips re-checking if the last check
    // happened within THROTTLE_MS -- nothing time-sensitive can change between two adds
    // seconds apart, so re-validating on every single click is pure waste. Checkout
    // arrival and Submit/Pay never pass this -- those are commitment points and must
    // always run fresh, no throttling.
    //
    // Runs for the group-order host (checked against the group's scheduledTime) and for
    // solo checkout (checked against the local checkout info) alike. Uses validTimeSlots
    // off a fresh shopping-cart fetch -- not just "already expired" -- so it also catches
    // a time still in the future but now inside the prep+delivery buffer.
    async checkStaleScheduledTime({ dispatch, getters, rootGetters, commit }, options) {
      if (options && options.throttle && (Date.now() - _lastStaleScheduleCheckAt) < STALE_SCHEDULE_THROTTLE_MS) {
        return false;
      }
      if (rootGetters.getPickupDeliveryEditDialog) return true;
      // Dine-in (including shared cart, which is dine-in-only) never sends scheduledTime
      // to the server -- see _fetchShoppingCartImpl's `state.orderType !== 'DINE_IN'` guard
      // below. checkoutInfo.scheduledTime can still hold a leftover value from before
      // dine-in/shared-cart started (e.g. a scheduled pickup time picked earlier in the
      // session), so without this guard the "time no longer available" dialog could fire
      // for an order type that was never going to use that time in the first place.
      if (getters.isDineIn) return false;
      const isInGroupOrder = rootGetters['groupOrder/isInGroupOrder'];
      const isGroupHost = isInGroupOrder && !!this.state.groupOrder.isHost;
      // Guests can't change the group's schedule, so there's nothing for them to
      // revalidate -- only the host's scheduledTime (or a solo order's) is checked.
      if (isInGroupOrder && !isGroupHost) return false;
      const scheduledTime = isGroupHost
        ? (this.state.groupOrder.group ? this.state.groupOrder.group.scheduledTime : null)
        : (getters.getCheckoutInfo && getters.getCheckoutInfo.scheduledTime);
      if (!scheduledTime || scheduledTime === 'now') return false;
      // validTimeSlots lives on serverCart (getValidTimeSlots), populated by
      // fetchShoppingCart -- NOT on state.bill (fetchBillView/fetchGroupOrderBillView),
      // which never carries this field. Group orders' bare fetchShoppingCart() no-ops
      // (_fetchShoppingCartImpl early-returns for them), so forceTimeSlots is required for
      // both paths to actually hit the API and refresh it.
      // This is the only branch that actually hits the network -- the throttle window
      // (see the top of this action) is measured from here, not from when the action was
      // merely called, so a structural "doesn't apply" case above (dine-in, group guest,
      // no scheduled time) never counts as a "recent check" for the next real caller.
      _lastStaleScheduleCheckAt = Date.now();
      await dispatch('fetchShoppingCart', { forceTimeSlots: true }).catch(() => {});
      const validTimeSlots = getters.getValidTimeSlots;
      if (!validTimeSlots || !validTimeSlots.day_slots) return false;
      const storeTz = (getters.getStore && getters.getStore.timeZone) || 'America/Toronto';
      const stale = isTimeStale(validTimeSlots, scheduledTime, storeTz, isGroupHost);
      if (!stale) return false;
      // Raw datetime, not a pre-formatted string -- PickupDeliveryEditDialog builds the
      // full "no longer available" sentence itself (date + bolded time, group-vs-solo
      // copy), so every caller just hands over what went stale.
      commit('SET_PICKUP_DELIVERY_EDIT_DIALOG_MESSAGE', scheduledTime, { root: true });
      commit('SET_PICKUP_DELIVERY_EDIT_DIALOG', true, { root: true });
      return true;
    },

    fetchShoppingCart({ dispatch }, options) {
      var key = options ? JSON.stringify(options) : ''
      var entry = _inFlightFetchShoppingCart[key]
      if (entry) {
        // DEV-11388: a call made while a request is in flight may carry newer cart state
        // (e.g. a coupon applied after the first request's body was built) -- joining the
        // in-flight request would silently drop it. Queue ONE trailing refetch instead;
        // any number of calls during the flight collapse into it, and it runs only after
        // the first settles, so responses can never land out of order.
        if (!entry.trailing) {
          entry.trailing = entry.promise.catch(function() {}).then(function() {
            return dispatch('fetchShoppingCart', options)
          })
        }
        return entry.trailing
      }
      var promise = dispatch('_fetchShoppingCartImpl', options).finally(function() {
        delete _inFlightFetchShoppingCart[key]
      })
      _inFlightFetchShoppingCart[key] = { promise: promise, trailing: null }
      return promise
    },
    async _fetchShoppingCartImpl({ rootState, state, commit, dispatch, getters, rootGetters }, options) {
      dispatch('checkTableNumExpired', true)
      if (rootGetters['groupOrder/isInGroupOrder'] && !(options && options.forceTimeSlots)) {
        return Promise.resolve();
      }
      if (rootGetters['sharedCart/isActive']) {
        return dispatch('sharedCart/refetchAndMerge', null, { root: true });
      }
      // A shared-cart session may still be restoring from localStorage after a
      // page refresh (App.vue's restoreSession call races this one). Try to
      // restore it directly rather than falling through to a normal cart fetch.
      if (state.orderType === 'DINE_IN' && getters.getTableId !== -1) {
        const restored = await dispatch('sharedCart/restoreSession', {}, { root: true })
        if (restored) {
          return dispatch('sharedCart/refetchAndMerge', null, { root: true })
        }
      }
      const storeId = window.params.sid || rootState.storeDetail.store.storeId || rootGetters.getStoreId;
      if (!storeId || storeId === 'null' || storeId === null) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[fetchShoppingCart] Skipped: no valid storeId available');
        }
        return Promise.resolve();
      }
      // Any dine-in table auto-joins/creates the store's shared-cart session
      // whenever the store has the feature on -- no invite link/QR needed,
      // scanning the table's regular QR code is enough (mirrors
      // StoreLocationDetails.vue's mounted() auto-join for the same reason).
      // Also requires posEnabled -- see isDineInSharedCartEnabled's comment in
      // StoreLocationDetails.vue for why (the session settles through POS).
      if (
        state.orderType === 'DINE_IN' &&
        getters.getTableId !== -1 &&
        !!get(rootState, 'storeDetail.store.posEnabled') &&
        !!get(rootState, 'storeDetail.store.settings.owaSettings.enableDineInSharedCart')
      ) {
        // Dynamic QR code tables (scan-table route) carry a signed, time-limited ?vc=
        // token the backend validates on the session's first fire/finalize -- must be
        // captured now, at session creation, or it's silently missing for the whole
        // session's lifetime (this early return skips the regular-cart vc read below).
        const scVcParams = new URLSearchParams(window.location.search)
        const scVc = scVcParams.get('vc')
        return dispatch('sharedCart/startSession', Object.assign(
          { storeId: storeId, tableId: getters.getTableId },
          scVc ? { vc: scVc } : {}
        ), { root: true })
      }
      if (state.orderType === 'DINE_IN' && getters.getTableId === -1) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[fetchShoppingCart] Skipped: dine-in order with tableId -1');
        }
        return Promise.resolve();
      }

      let tableNo = null;
      if (this && this.$router && this.$router.currentRoute && this.$router.currentRoute.query && this.$router.currentRoute.query.tid) {
        tableNo = this.$router.currentRoute.query.tid;
      } else {
        tableNo = getters.getTableId;
      }
      let _order = {
        storeId: storeId,
        subOrderList: [],
        // orderTypeOverride lets a caller preview slots for a NOT-YET-COMMITTED order type
        // (e.g. PickupDeliveryEditDialog confirming a group-order switch, where state.orderType
        // intentionally isn't flipped until Done) without touching state.orderType itself.
        // Default order type is delivery.
        orderType: (options && options.orderTypeOverride) || state.orderType || 'DELIVERY'
      };

      if (state.orderType === 'DINE_IN' && tableNo && tableNo !== -1) {
        _order.tableNo = tableNo;
      }

      const { scheduledTime, scheduledDay } = getters.getCheckoutInfo
      let realScheduledTime = scheduledTime === "now" ? "" : scheduledTime;
      if (realScheduledTime && scheduledDay && state.orderType !== 'DINE_IN') {
        if (rootGetters['groupOrder/isInGroupOrder']) {
          // In a group order the server owns the scheduled time — don't send it from the client
          // to avoid corrupting it with a locally-encoded value
        } else {
          const hours = moment(realScheduledTime).hours();
          const minutes = moment(realScheduledTime).minutes();
          const orderDateTime = moment(scheduledDay);
          orderDateTime.hours(hours);
          orderDateTime.minutes(minutes);
          orderDateTime.seconds(0);
          const targetTimeZone = getters.getStore.timeZone;
          if (targetTimeZone) {
            orderDateTime.tz(targetTimeZone, true);
          }
          _order.scheduledTime = orderDateTime.toISOString();
        }
      }
      const urlParams = new URLSearchParams(window.location.search);
      const vc = urlParams.get("vc");
      if (vc) {
        _order.vc = vc;
      }
      const uniqueStoreId = rootGetters.getUniqueStoreId
      const storeCart = uniqueStoreId
        ? state.carts[ uniqueStoreId ] || []
        : state.localCart

      let allCoupons = [...getters.getCoupons];
      const inputCouponCode = getters.getCouponCode;
      if (inputCouponCode) {
        const existingCouponCodes = allCoupons.map(c => c.code);
        if (!existingCouponCodes.includes(inputCouponCode)) {
          allCoupons.push({
            code: inputCouponCode,
            qty: 1,
            orderItem: null
          });
        }
      }

      _order.subOrderList = [{
        seq: (getters.getExistingOrder.length || 0)+1,
        orderItemList: storeCart.map(cartItem => buildMenuStructure(getters.findFullItem(cartItem))),
        punchCards: getters.getActivePunchCards(),
        coupons: parseCoupons(allCoupons),
        redemptionItems: buildRedemptionItems(getters.getActiveRewards)
      }];

      if (getters.getExistingOrder.length) {
        for(var x = 0; x < getters.getExistingOrder.length; x++) {
          _order.subOrderList.push({
            seq: x+1,
            orderItemList: get(getters.getExistingOrder, `[${x}].orderItemList`, []),
            punchCards: get(getters.getExistingOrder, `[${x}].punchCards`, []),
            coupons: get(getters.getExistingOrder, `[${x}].coupons`, []),
            redemptionItems: get(getters.getExistingOrder, `[${x}].redemptionItems`, [])
          })
        }
      }

      // If this is a delivery and the delivery address is set, provide the address
      if(!isEmpty(state.deliveryAddress)) {
        _order.deliveryAddress = parseDeliveryAddress({...state.deliveryAddress}, getters.getDeliveryAddress, getters.getDeliveryAddressResult)
      }


      const storeCouponCode = inputCouponCode || (allCoupons.length > 0 ? allCoupons[0].code : null);
      _order.couponCode = storeCouponCode || null

      if (getters.isDelivery && getters.getDeliveryProvider != null)
        _order.deliveryProvider = getters.getStoreDeliveryProvider

      commit('SET_CART_LOADING', true)
      let menuQuery = "";
      const menuCode = (getters.getMenu && getters.getMenu.code) ||
        (this.$router && this.$router.currentRoute && this.$router.currentRoute.params && this.$router.currentRoute.params.mid)
      if (menuCode) {
        menuQuery = `&menu=${menuCode}`;
      }
      return this.$api.post(`/shoppingcart?validateScheduleTime=true${menuQuery}`, _order).then(res => {
        commit('SET_SERVER_CART', res.data)

        if (res.data && res.data.subOrderList) {
          const items = get(res, 'data.subOrderList[0].orderItemList', [])
          const errors = []
          items.forEach(item => {
            if (item.errors && Array.isArray(item.errors)) {
              item.errors.forEach(err => { errors.push(`- ${err.errorMsg}`) })
            }
          })

          if (errors.length > 0) {
            const errorString = '<br />' + errors.join('<br />')
            dispatch('showSnack', {text: 'Cart error: ' + errorString, color: 'error'})
          }

          const { serviceCharges = [] } = res.data
          commit('SET_SERVICE_CHARGES', serviceCharges)
          dispatch('mergeServerWithLocalCart')
        }
      }).catch(error => {
        if (error.response && error.response.data) {
          if(error.response.data.errorCode === "ORD_ERR_INVALID_DYNAMIC_QRCODE_VC") {
            const storeId = rootGetters.getUniqueStoreId;
            const tempSlots = getters.getValidTimeSlots;

            dispatch("clearCheckoutInfo");
            // DEV-11453: put the defaults (ASAP/today) straight back, as the router does after
            // every clearCheckoutInfo. Left empty, every menu with hours showed the "only
            // available during specific hours" popup -- even while open -- until a reload.
            dispatch("setDefaultCheckoutInfoIfEmpty");
            commit("RESET_CART", { storeId, tempSlots });
            commit("SET_ACTIVE_COUPON", null);
            commit("SET_ACTIVE_REWARDS", null);
          }
          // Auth errors are handled globally by the response interceptor
          const _errorMsg = get(error, 'response.data.errorMsg')
          const _errorCode = get(error, 'response.data.errorCode')
          const _isAuthError = error.response.status === 401
            || _errorCode === 'USR_ERR_0030'
            || (typeof _errorMsg === 'string' && _errorMsg.toLowerCase().includes('invalid authentication token'))
          const _isScheduledTimeError = _errorCode === 'ORD_ERR_SCHEDULED_TIME_INVALID' || _errorCode === 'ORD_ERR_SCHEDULED_TIME_NOT_ALLOWED'
          if (!_isAuthError && !_isScheduledTimeError) {
            dispatch('showSnack', {text: 'Cart error: ' + (_errorMsg || 'An unexpected error occurred'), color: 'error'})
          }

          // If the request failed because of the coupon, remove it
          if (error.response.data.errorCode == 'COUPON_ERROR_COUPON_DEFINITION_NOT_FOUND') {
            dispatch('setCouponCode', {
              storeId: storeId,
              couponCode: null
            })
          }
          if (error.response.data.errorCode == 'ORD_ERR_SCHEDULED_TIME_INVALID') {
            if (!rootGetters['groupOrder/isInGroupOrder']) {
              dispatch('resetCart')
              dispatch('clearCheckoutInfo')
            }
            throw error
          }

          //If the request failed because of the scheduled time, remove it
          if (error.response.data.errorCode === 'ORD_ERR_SCHEDULED_TIME_NOT_ALLOWED') {
            if (!rootGetters['groupOrder/isInGroupOrder']) {
              dispatch("clearScheduledTimeandDay");
              // DEV-11365: this request never replaced serverCart, so the cart on screen is still
              // the previous one (possibly priced for another order type). Refetch now that the
              // time is cleared -- no scheduled time is sent, so this error can't repeat.
              dispatch('fetchShoppingCart')
            }
            throw error
          }
        }
      })
      .finally(() => {
        commit('SET_CART_LOADING', false)
      });
    },
    fetchPromotions({ commit, getters }) {
      const orderType = getters.getOrderType || 'PICKUP'
      return this.$api.get(`/promotions?orderType=${orderType}`).then(res => {
        commit('SET_PROMOTIONS', res.data || [])
      }).catch(error => {
        console.error('[fetchPromotions] Error:', error)
        commit('SET_PROMOTIONS', [])
      })
    },
    fetchGroupOrderBillView({ rootState, commit, rootGetters, dispatch }) {
      const goState = rootGetters['groupOrder/isInGroupOrder'] && rootState.groupOrder
      if (!goState) return Promise.resolve()
      const inviteUuid = goState.inviteUuid
      if (!inviteUuid) return Promise.resolve()
      const body = {
        expectedGroupVersion: goState.group ? goState.group.version : null,
        hostParticipantCartOrderId: goState.participantCartOrderId || null
      }
      // See _billRequestToken's own comment -- only the call still holding the latest
      // token when its promise settles is allowed to commit anything.
      const requestToken = ++_billRequestToken
      commit('SET_BILL_LOADING', true)
      commit('SET_BILL_LOAD_ERROR', false)
      return this.$api.post(`/group-orders/${inviteUuid}/bill/view`, body)
        .then(res => {
          if (requestToken !== _billRequestToken) return
          commit('SET_BILL', res.data.bill || res.data)
        })
        .catch(error => {
          if (requestToken === _billRequestToken) commit('SET_BILL_LOAD_ERROR', true)
          throw error
        })
        .finally(() => {
          if (requestToken === _billRequestToken) commit('SET_BILL_LOADING', false)
        })
    },
    fetchBillView({ rootState, state, commit, getters, rootGetters }) {
      let _order
      const storeId = rootGetters.getUniqueStoreId
      const storeCart = storeId
        ? state.carts[ storeId ] || []
        : state.localCart

      // Collect coupons from both systems for bill view (declare at function scope)
      let billCoupons = [...getters.getCoupons]; // From activeCoupon system
      const inputCouponCodeBill = getters.getCouponCode;
      
      // Add couponCode system coupon if it exists and isn't already in activeCoupons
      if (inputCouponCodeBill) {
        const existingCouponCodes = billCoupons.map(c => c.code);
        if (!existingCouponCodes.includes(inputCouponCodeBill)) {
          billCoupons.push({
            code: inputCouponCodeBill,
            qty: 1,
            orderItem: null
          });
        }
      }

      if(!getters.isDineInAndExistingOrder){
        _order = {
          customerId: rootState.auth.user.id,
          storeId: get(this, '$router.history.current.params.sid', +getters.getStoreId),
          subOrderList: [],
          tableNo: getCurrentTableNo(state, getters),
          orderType: state.orderType,
          takeout: getters.getCheckoutInfo.takeout || null
        };

        let _orderItemList = storeCart.map(cartItem => buildMenuStructure(getters.findFullItem(cartItem)))

        _order.subOrderList = [{
          orderItemList: _orderItemList,
          punchCards: getters.getActivePunchCards(),
          coupons: parseCoupons(billCoupons),
          redemptionItems: buildRedemptionItems(getters.getActiveRewards)
        }];
      } else {
        _order = getActiveMultiOrder(state)
      }

      if(!isEmpty(state.deliveryAddress)) {
        _order.deliveryAddress = parseDeliveryAddress({...state.deliveryAddress}, getters.getDeliveryAddress, getters.getDeliveryAddressResult)
      }

      if (getters.isDelivery && getters.getDeliveryProvider != null)
        _order.deliveryProvider = getters.getStoreDeliveryProvider

      // Set couponCode from either system for bill view
      const storeCouponCodeBill = inputCouponCodeBill || (billCoupons.length > 0 ? billCoupons[0].code : null);
      _order.couponCode = storeCouponCodeBill || null
      const urlParams = new URLSearchParams(window.location.search);
      const vc = urlParams.get("vc");
      if (vc) {
        _order.vc = vc;
      }

      // See _billRequestToken's own comment -- only the call still holding the latest
      // token when its promise settles is allowed to commit anything. Shared with
      // fetchGroupOrderBillView above since only one bill is ever being displayed at a
      // time (solo vs. group), so their requests are still ordered against each other.
      const requestToken = ++_billRequestToken
      commit('SET_BILL_LOADING', true)
      commit('SET_BILL_LOAD_ERROR', false)
      return this.$api.post('/bill/view', _order)
        .then(res => {
          if (requestToken !== _billRequestToken) return
          commit('SET_BILL', res.data);
        })
        .catch(error => {
          if (requestToken === _billRequestToken) commit('SET_BILL_LOAD_ERROR', true)
          throw error
        })
        .finally(() => {
          if (requestToken === _billRequestToken) commit('SET_BILL_LOADING', false)
        })
    },


    async payOrder({ rootState, state, commit, getters, dispatch, rootGetters }, checkoutForm ) {
      // Last line of defence: a table-QR guest's order always reaches POS as DINE_IN with
      // its table, whatever state drifted to. Logged so the drift itself gets found.
      if (!rootGetters['sharedCart/isActive']) {
        const driftedFrom = state.orderType
        if (await dispatch('enforceTableOrderType') && driftedFrom !== 'DINE_IN') {
          this.$api.post('/order/error', {
            error: `Corrected orderType ${driftedFrom} -> DINE_IN on table route`,
            statusCode: -1,
            storeId: get(rootState, 'storeDetail.store.storeId', -1)
          }).catch(() => {})
        }
      }
      // A blank/unknown orderType is saved server-side as null/DINE_IN and lands in the
      // tablet's Dine In list, so never submit without an explicit, known type.
      if (!rootGetters['sharedCart/isActive'] && !ORDER_TYPES.some(type => type.value === state.orderType)) {
        // Restore the app's normal default (PICKUP first) so the guest can see it and resubmit.
        this.$api.post('/order/error', {
          error: `Blocked checkout with invalid orderType: ${state.orderType}`,
          statusCode: -1,
          storeId: get(rootState, 'storeDetail.store.storeId', -1)
        }).catch(() => {})
        dispatch('setOrderTypeIfNotSet')
        commit('SET_DIALOG_MESSAGE', { title: 'Error Occured', body: 'Please confirm your order type and submit again.' })
        return Promise.reject(new Error(`Invalid orderType: ${state.orderType}`))
      }
      try {
        commit('SET_CART_LOADING', true)

        try {
          const total = getters.getBillTotal
          const currency = get(rootGetters, 'getStore.currency', 'CAD')
          if (total) {
            window.localStorage.setItem('lastOrderTotal', total)
            window.localStorage.setItem('lastOrderCurrency', currency)
          }
        } catch (e) {}

        if (!window.navigator.onLine) {
          dispatch('showSnack', {
            text: 'Failed to connect to server',
            color: 'error',
            timeout: 0
          })
        }

        const combineStrings = (a, b, delim) => {
          let temp = a
          if (!!temp)
            temp += delim
          return temp + b
        }

        if (checkoutForm === 'replace')
          checkoutForm = {
            ...checkoutFormBlank,
            orderNotes: getters.getCheckoutInfo.orderNotes,
            takeout: getters.getCheckoutInfo.takeout || null
          }

        let _order
        const storeId = rootGetters.getUniqueStoreId
        const storeCart = storeId ? state.carts[storeId] || [] : state.localCart

        // Collect coupons from both systems for payment (declare at function scope)
        let paymentCoupons = [...getters.getCoupons]; // From activeCoupon system
        const inputCouponCodePayment = getters.getCouponCode;
        
        // Add couponCode system coupon if it exists and isn't already in activeCoupons
        if (inputCouponCodePayment) {
          const existingCouponCodes = paymentCoupons.map(c => c.code);
          if (!existingCouponCodes.includes(inputCouponCodePayment)) {
            paymentCoupons.push({
              code: inputCouponCodePayment,
              qty: 1,
              orderItem: null
            });
          }
        }

        if (!getters.isDineInAndExistingOrder) {
          _order = {
            customerId: rootState.auth.user.id,
            storeId: rootState.storeDetail.store.storeId,
            subOrderList: [],
            tableNo: getCurrentTableNo(state, getters),
            orderType: state.orderType,
            telephone: checkoutForm.phoneNumber || get(rootState.auth, 'user.telephone'),
            deliveryAddress: parseDeliveryAddress(
                              {...state.deliveryAddress},
                              getters.getDeliveryAddress,
                              getters.getDeliveryAddressResult,
                              get(checkoutForm, 'unitNumber')
                            ),
            scheduledTime: checkoutForm.scheduledTime === 'now' ? '' : checkoutForm.scheduledTime,
            customerName: combineStrings(get(checkoutForm, 'firstname',''), get(checkoutForm, 'lastname',''), ' '),
            includeCutlery: checkoutForm.includeCutlery,
            takeout: getters.getCheckoutInfo.takeout || null
          };

          // Pull the referrer reference from the query params
          const referrer = getUrlParameter('referrer')
          if (referrer)
            _order.referrer = referrer

          if ('optOutRewards' in checkoutForm)
            _order.optOutRewards = checkoutForm.optOutRewards

          _order.subOrderList = [{
            orderItemList: storeCart.map(cartItem => buildMenuStructure(getters.findFullItem(cartItem))),
            punchCards: getters.getActivePunchCards(),
            coupons: parseCoupons(paymentCoupons),
            redemptionItems: buildRedemptionItems(getters.getActiveRewards)
          }];
        } else {
          _order = getActiveMultiOrder(state)
          _order.takeout = getters.getCheckoutInfo.takeout || null;

        }
        const urlParams = new URLSearchParams(window.location.search);
        const vc = urlParams.get("vc");
        if (vc) {
          _order.vc = vc;
        }

        let _paymentReq
        const activeMultiOrder = getActiveMultiOrder(state)
        if(activeMultiOrder) {
          _paymentReq = {
            orderId: activeMultiOrder.orderId,
            tipsAmount: getters.getTipsAmount,
            tipsForStore: getters.getTipsForStoreAmount,
            email: get(checkoutForm, 'email')
          }
        } else {
          _paymentReq = {
            order: _order,
            tipsAmount: getters.getTipsAmount,
            tipsForStore: getters.getTipsForStoreAmount,
            email: get(checkoutForm, 'email')
          }
        }

        const { scheduledTime, scheduledDay } = checkoutForm

        if (scheduledTime instanceof Date && scheduledDay instanceof Date) {
          const hours = scheduledTime.getHours()
          const minutes = scheduledTime.getMinutes()

          const orderDateTime = moment(scheduledDay)
          orderDateTime.hours(hours)
          orderDateTime.minutes(minutes)
          orderDateTime.seconds(0)
          const targetTimeZone = getters.getStore.timeZone;
          if (targetTimeZone) {
            orderDateTime.tz(targetTimeZone, true);
          }
          _paymentReq.order.scheduledTime = orderDateTime.toISOString()
        } else if(_paymentReq.order) {
          delete _paymentReq.order.scheduledTime
        }

        if (get(checkoutForm, 'selectedPayMethodId'))
          _paymentReq.paymentMethodId = checkoutForm.selectedPayMethodId;

        if (isSelectedMethod('membership', getters)) {
          // If memebership is sufficient for bill, we don't need any other payment types
          if (getters.getCustomerRewards.balance >= getters.getBillTotal)
            _paymentReq.paymentMethodId = null

          _paymentReq.paidByMembership = true
          // DEV-10377: the guest chose to use less ("Apply $X instead") so the card keeps
          // Stripe's minimum -- the backend splits on this amount instead of the full balance.
          if (getters.getMembershipPayAmountRequest != null)
            _paymentReq.membershipPayAmount = getters.getMembershipPayAmountRequest
        }

        // this check overrides above logic when offline to make paymentMethodId null
        if (isSelectedMethod('offline', getters)) {
          _paymentReq.email = get(checkoutForm, 'email')

          // We use reCAPTCHA to authenticate offline payments to avoid malicuous behaviour
          const recaptcha = await load('6LeG1m4rAAAAAEsvXtWYVz2wAzFCh5Fpd-4_urx2', { autoHideBadge: true, useEnterprise: true });
          if (!recaptcha || !recaptcha.siteKey) {
            this.SET_DIALOG_MESSAGE({
              title: "Error Occured",
              body: 'Captcha Error',
              message: 'Please, try again'
            })
            return
          }
          const CAPTCHA_ACTION = {
            PAY_ORDER: 'PAY_ORDER',
          };

          _paymentReq.captcha = await recaptcha.execute(CAPTCHA_ACTION.PAY_ORDER);
        }

        if (isSelectedMethod('wechat', getters)) {
          _paymentReq.payByWechat = true
          _paymentReq.paymentMethodId = undefined
        }

        if ('order' in _paymentReq) {
          // Apple Pay?
          if (checkoutForm.paymentToken) {
            _paymentReq.paymentToken = checkoutForm.paymentToken
            _paymentReq.paymentMethodId = undefined
          }

          _paymentReq.order.orderNotes = get(checkoutForm, 'orderNotes')
          _paymentReq.order.deliveryInstructions = combineStrings(get(checkoutForm, 'deliveryNotes', ''), get(checkoutForm, 'deliveryMessage', ''), ". ")

          const paymentCouponCode = inputCouponCodePayment || (paymentCoupons.length > 0 ? paymentCoupons[0].code : null);
          if (paymentCouponCode)
            _paymentReq.order.couponCode = paymentCouponCode

          const urlParams = new URLSearchParams(window.location.search);
          const vc = urlParams.get("vc");
          if (vc) {
            _paymentReq.order.vc = vc;
          }
          // Send idempotency token with order
          _paymentReq.order.idempotencyToken = state.orderUUID
          _paymentReq.order.clientSubmitTime = Date.now()
        }

        _paymentReq.returnUrl = `${window.location.origin}/owa/snappy/detail/${getters.getStore.groupId}/${getters.getStore.storeId}/3ds_success_handler`

        _paymentReq.kioskMode = getters.isInKioskMode

        if (getters.isDelivery && getters.getDeliveryProvider != null)
          _paymentReq.order.deliveryProvider = getters.getStoreDeliveryProvider

        // Snapshot cart + bill for GA4 purchase event BEFORE cart/bill are cleared
        try {
          const cartItems = (getters.getLocalCart || []).map(cartItem => toGa4Item(getters, cartItem))
          const billTotal = Number(get(getters, 'getBill.subTotalAmount', get(getters, 'getBill.totalAmount', 0)))
          const activeCoupons = getters.getActiveCoupon || []
          const couponCode = activeCoupons.map(c => c.code || '').filter(Boolean).join(',')
          const couponName = activeCoupons.map(c => c.name || '').filter(Boolean).join(',')
          const couponUsed = activeCoupons.length > 0
          window.localStorage.setItem('ga4_purchase_snapshot', JSON.stringify({ cartItems, billTotal, couponCode, couponName, couponUsed }))
        } catch (e) { console.warn('GA4 snapshot error', e) }

        // Shared cart dine-in: finalize the session instead of the normal payment flow
        if (rootGetters['sharedCart/isActive']) {
          return dispatch('sharedCart/finalizeSession', {
            email: get(checkoutForm, 'email'),
            tipsAmount: getters.getTipsAmount,
            tipsForStore: getters.getTipsForStoreAmount,
            estimatedTotal: getters.getBillTotal,
            kioskMode: getters.isInKioskMode
          }, { root: true }).then(async res => {
            dispatch('lookForOrderRewards', res)
            dispatch('setSuccessfulOrderDialog', { data: res })
            if (getters.isLoggedIn === false) dispatch('pushToGuestHistory', res)
            dispatch('removePunchCards')
            return res
          }).catch(error => {
            commit('SET_CART_LOADING', false)
            const errorMsg = get(error, 'response.data.errorMsg')
            if (errorMsg) {
              commit('SET_DIALOG_MESSAGE', { body: errorMsg, code: get(error, 'response.data.errorCode') })
            } else {
              commit('SET_DIALOG_MESSAGE', { body: get(error, 'message', 'Failed to complete payment, please try again') })
            }
            return Promise.reject(error)
          })
        }

        return this.$api.post('/payment/order', _paymentReq).then(async res => {
          if (res.data.paymentType == 'WECHAT') {
            // Redirect to wechat to pay with their account
            const { redirectUrl } = res.data

            if (redirectUrl)
              window.location = redirectUrl
          } else if (res.data.paymentType == 'GLOBAL_PAYMENTS') {
            // Handle Global Payments 3ds
            // Show iframe to global payments 3ds to authenticate card
            commit('SET_STRIPE_INFO', {
              paymentReq: _paymentReq,
              paymentResp: res.data
            })

            commit('SET_IFRAME', res.data.redirectUrl)
          } else {
            if (res.data.intentSecret) {
              let stripe = await loadStripe(process.env.VUE_APP_STRIPE, !res.data.destination ? { stripeAccount: res.data.stripeAccountId } : null);

              return stripe.confirmCardPayment(res.data.intentSecret).then((result) => {
                // Handle result.error or result.paymentIntent
                const status = get(result, 'paymentIntent.status')

                if (status === 'succeeded' || status === 'requires_capture') {
                  // Show your customer that the payment has succeeded
                  dispatch('lookForOrderRewards', res.data)

                  dispatch('setSuccessfulOrderDialog', {
                    ..._paymentReq,
                    data: res.data
                  });
                } else {
                  commit('SET_DIALOG_MESSAGE', {
                    title: 'Error Occured',
                    body: get(result, 'error.message', 'Failed to complete payment, please check your info and try again')
                  })

                  this.$api.post('/order/error', {
                    error: 'Stripe 3DS Failure Status: '+get(result, 'error.code'),
                    statusCode: -1,
                    storeId: get(getters.getStore, 'storeId', 0)
                  })
                }
              });
            } else {
              // No further actions needed. Payment completed
              dispatch('lookForOrderRewards', res.data)

              dispatch('setSuccessfulOrderDialog', {
                ..._paymentReq,
                data: res.data
              });
            }
          }
          if (res.data.paymentType !== 'STRIPE' && res.data.paymentType !== 'GLOBAL_PAYMENTS' && res.data.paymentType !== 'WECHAT') {
            dispatch('lookForOrderRewards', res.data)
            
            dispatch('setSuccessfulOrderDialog', {
              ..._paymentReq,
              data: res.data
            });
          }

          // Check if the order was made as guest
          // If so, we track order history in localstorage as their is no way of
          // identifying them with the standard format
          if (getters.isLoggedIn === false)
            dispatch('pushToGuestHistory', res.data)

          dispatch('removePunchCards')

          return res
        }).catch(error => {
          commit('SET_CART_LOADING', false)

          this.$api.post('/order/error', {
            error: ''+findErrorMessage(error),
            statusCode: get(error, 'response.status', -1),
            storeId: get(rootState, 'storeDetail.store.storeId', -1)
          })

          if (error.response && error.response.data) {
            const responseData = get(error, 'response.data', {})
            const errorMsg = get(responseData, 'errorMsg')

            // Handled by the caller (CheckoutPage submitOrder) via the shared
            // stale-time recovery dialog -- skip the generic error dialog,
            // same suppression pattern as ORD_ERR_SCHEDULED_TIME_INVALID/NOT_ALLOWED above.
            if (responseData.errorCode === 'ORD_ERR_SCHEDULED_TIME_TOO_CLOSE') {
              return Promise.reject(error)
            }

            const getErrorMessages = () => {
              return get(responseData, 'errors', []).reduce((prev, curr) => {
                return [ ...prev, `- ${curr.errorMsg}` ]
              }, [])
            }

            const errors = errorMsg ? [errorMsg] : getErrorMessages()

            commit('SET_DIALOG_MESSAGE', {
              body: errors.join('<br />'),
              code: responseData.errorCode
            })
          } else if (error.request || error.message) {
            console.error('error: '+error.request || error.message);

            // The request was made but no response was received
            commit('SET_DIALOG_MESSAGE', {
              body: `Uh oh, something went wrong on getting a response from the server due to: ${error.request || error.message}.<br><br>Your order still might have been successfully submitted.`,
              code: error.code
            })
          }

          return Promise.reject(error)
        })
        .finally(() => {
          commit('SET_CART_LOADING', false)
        })
      } catch (error) {
        commit('SET_CART_LOADING', false)
        console.error('Pay Order Failed: ');
        console.error(`Failed to submit order: pay_order_error_-100\n\nMessage: ${findErrorMessage(error)}\n\nStack: ${error.stack}`);

        commit('SET_DIALOG_MESSAGE', {
          body: `Failed to submit order: ${findErrorMessage(error)}`,
          code: `pay_order_error_-100`
        })

        this.$api.post('/order/error', {
          error: `Failed to submit order: pay_order_error_-100\n\nMessage: ${findErrorMessage(error)}\n\nStack: ${error.stack}`,
          statusCode: -100,
          storeId: get(rootState, 'storeDetail.store.storeId', -1)
        })
      }
    },
    lookForOrderRewards({ rootState, commit, getters, dispatch }, data) {
      const handleCleanUp = (route = 'menu-selection') => {
        const tid = get(
          this,
          "$router.history.current.query.tid",
          undefined
        );
        const dineInOnly = get(
          this,
          "$router.history.current.query.dineInOnly",
          undefined
        );
        const orderId = get(
          this,
          "$router.history.current.query.orderId",
          undefined
        );
        const displayId = get(
          this,
          "$router.history.current.query.displayId",
          undefined
        );
        const urlParams = new URLSearchParams(window.location.search);
        const vc = urlParams.get("vc");
        commit('SET_PAYMENT_TYPE', null)
        dispatch('clearCheckoutInfo')
        dispatch('resetCart')
        
        // Build query object and only add parameters that have values
        const queryObj = { skipCartFetch: true };
        if (tid) queryObj.tid = tid;
        if (dineInOnly) queryObj.dineInOnly = dineInOnly;
        if (vc) queryObj.vc = vc;
        if (orderId) queryObj.orderId = orderId;
        if (displayId) queryObj.displayId = displayId;
        
        this.$router.push({
          name: route,
          params: {
            gid: get(rootState, "storeDetail.store.groupId", 0),
            sid: get(rootState, "storeDetail.store.storeId", 0),
            restaurantSlug: get(rootState, "storeDetail.store.slug", "")
          },
          query: queryObj
        }).catch(() => {});
      }

      if ('orderBonus' in data && !!data.orderBonus) {
        // If the user was rewarded coupons, show them the order bonus dialog first
        commit('SET_ORDER_REWARDS', data.orderBonus)
        commit('SET_ORDER_BONUS_DIALOG', true)
        handleCleanUp()
      } else {
        handleCleanUp()
      }
    },
    setOrderTypeIfNotSet({ getters, dispatch, rootGetters }) {
      if (rootGetters['groupOrder/isInGroupOrder']) return

      const storeOrderTypes = getters.getStoreOrderTypes
      const currentType = getters.getOrderType

      const isCurrentValid = storeOrderTypes.findIndex(t => t.value === currentType) !== -1

      if (storeOrderTypes.length > 0 && (isEmpty(currentType) || !isCurrentValid)) {
        const pickupType = storeOrderTypes.find((type) => type.value === "PICKUP");
        const type = pickupType || storeOrderTypes[0];
        dispatch('setOrderType', type.value)
      }
    },
    setCartAnimation({commit}) {
      commit('SET_CART_ANIMATION')
    },
    ensureStoreCart({commit, rootGetters}) {
      const storeId = rootGetters.getUniqueStoreId
      commit('ENSURE_STORE_CART', storeId)
    },

    setExistingOrder({commit, dispatch, getters, rootState}) {
      const store = Object.assign({}, rootState.storeDetail.store)

      return this.$api.get('/customer/order').then(res => {
        // commit('SET_SUB_ORDER', res.data[0])

        if (!_.isEmpty(res.data[0])) {
          const tableId = res.data[0].tableNo
          const payload = { tableId, store }
          commit('SET_TABLE_ID', payload)
        }
      }).catch(error => {
        if (error.response && error.response.data) {
          // Auth errors are handled globally by the response interceptor
          const _cErrorMsg = get(error, 'response.data.errorMsg')
          const _cErrorCode = get(error, 'response.data.errorCode')
          const _cIsAuthError = error.response.status === 401
            || _cErrorCode === 'USR_ERR_0030'
            || (typeof _cErrorMsg === 'string' && _cErrorMsg.toLowerCase().includes('invalid authentication token'))
          if (!_cIsAuthError) {
            dispatch('showSnack', {text: 'Checkout error: ' + (_cErrorMsg || 'An unexpected error occurred'), color: 'error'})
          }
          if (getters.getOrderType) {
            return dispatch('fetchShoppingCart')
          }
        }
      })
    },

    updateSubOrder({ rootState, state, commit, getters, rootGetters, dispatch }, orderId) {
      if (rootGetters['sharedCart/isActive']) {
        return dispatch('sharedCart/submitRound', {}, { root: true })
      }

      let _order = {};

      const storeId = rootGetters.getUniqueStoreId
      const storeCart = storeId
        ? state.carts[ storeId ] || []
        : state.localCart

      // let _orderItemList = storeCart.map(cartItem => buildMenuStructure(getters.findFullItem(cartItem)))
      //
      // _order.orderItemList =  _orderItemList
      let updateCoupons = [...getters.getCoupons];
      const inputCouponCodeUpdate = getters.getCouponCode;
      if (inputCouponCodeUpdate) {
        const existingCouponCodes = updateCoupons.map(c => c.code);
        if (!existingCouponCodes.includes(inputCouponCodeUpdate)) {
          updateCoupons.push({
            code: inputCouponCodeUpdate,
            qty: 1,
            orderItem: null
          });
        }
      }

      return this.$api.post(`/order/${orderId}`, {
        orderItemList: storeCart.map(cartItem => buildMenuStructure(getters.findFullItem(cartItem))),
        punchCards: getters.getActivePunchCards(),
        coupons: parseCoupons(updateCoupons),
        redemptionItems: buildRedemptionItems(getters.getActiveRewards),
        takeout: getters.getCheckoutInfo.takeout || null
      }).then(res => {
        state.carts[ storeId ] = []
        commit('SET_SUB_ORDER', res.data)

        // dispatch('fetchShoppingCart')
      });
    },
    createSubOrder({ rootState, state, commit, getters, rootGetters, dispatch }) {
      if (rootGetters['sharedCart/isActive']) {
        return dispatch('sharedCart/submitRound', {}, { root: true })
      }

      let _order

      _order = {
        customerId: rootState.auth.user.id,
        storeId: rootState.storeDetail.store.storeId,
        subOrderList: [],
        tableNo: getters.getTableId,
        orderType: state.orderType
      };

      const storeId = rootGetters.getUniqueStoreId
      const storeCart = storeId
        ? state.carts[ storeId ] || []
        : state.localCart

      let _orderItemList = storeCart.map(cartItem => buildMenuStructure(getters.findFullItem(cartItem)))

      _order.subOrderList = [{
        orderItemList: _orderItemList
      }];
      
      const urlParams = new URLSearchParams(window.location.search);
      const vc = urlParams.get("vc");
      const tid = get(this, "$router.history.current.query.tid", null);
      let createCoupons = [...getters.getCoupons]; 
      const inputCouponCodeCreate = getters.getCouponCode;
      if (inputCouponCodeCreate) {
        const existingCouponCodes = createCoupons.map(c => c.code);
        if (!existingCouponCodes.includes(inputCouponCodeCreate)) {
          createCoupons.push({
            code: inputCouponCodeCreate,
            qty: 1,
            orderItem: null
          });
        }
      }

      return this.$api.post('/order', {
        'subOrderList': [{
          orderItemList: storeCart.map(cartItem => buildMenuStructure(getters.findFullItem(cartItem))),
          punchCards: getters.getActivePunchCards(),
          coupons: parseCoupons(createCoupons),
          redemptionItems: buildRedemptionItems(getters.getActiveRewards)
        }],
        vc,
        tableNo: tid,
        orderType: state.orderType,
        takeout: getters.getCheckoutInfo.takeout || null
      }).then(res => {
        state.carts[ storeId ] = []
        commit('SET_SUB_ORDER', res.data)

        // dispatch('fetchShoppingCart')
      }).catch(e => {
        console.error(e)
      })
    },
    prepNextOrder({ commit, getters, dispatch}) {
      dispatch('removeCoupon')
      commit('SET_ACTIVE_REWARDS', null)
      dispatch('removePunchCards')

      dispatch('fetchShoppingCart')
    },

    resetStoreSubOrder({ commit, rootGetters }){
      const storeId = rootGetters.getUniqueStoreId
      commit('RESET_SUB_ORDER', storeId)
    },
    setInStore({commit}, inStore) {
      commit('SET_IN_STORE', inStore)
    },

    pushToGuestHistory({commit, getters}, order = {}) {
      const storeId = get(getters, 'getStore.storeId', -1)
      const guestOrderHistory = JSON.parse(localStorage.getItem('guestOrderHistory')) || {}

      if (storeId !== -1) {
        const storeHistory = guestOrderHistory[storeId] || []

        storeHistory.unshift(order.orderId)

        guestOrderHistory[storeId] = storeHistory

        localStorage.setItem('guestOrderHistory', JSON.stringify(guestOrderHistory))
    }
    }
  },

  mutations: {
    SET_BILL_LOADING(state, val) {
      state.isBillLoading = !!val
    },
    SET_BILL_LOAD_ERROR(state, val) {
      state.isBillLoadError = !!val
    },
    SET_MEMBERSHIP_PAY_CAP(state, amount) {
      state.membershipPayCap = amount
    },
    SET_COUPON_CODE(state, data) {
      const { storeId, couponCode } = data || {}

      if (storeId) {
        state.couponCodes = {
          ...state.couponCodes,
          [storeId]: couponCode
        }
      }
    },
    SET_TABLE_ID(state, payload) {
      const { store, tableId } = payload
      const sections = get(store, 'sections', []) || []
      const tables = sections.map(section => section.tables).flat()

      const matchDisplay = (tables, tableId) => {
        if (!!tables.length) {
          const table = tables.find(table => table.tableNo == tableId)

          if (table)
            return table.displayTableNo
        }

        return tableId
      }

      state.tableId = tableId
      state.tableSetAt = new Date().getTime();
      state.tableDisplay = matchDisplay(tables, tableId)
    },
    CLEAR_TABLE_ID(state) {
      state.tableId = -1
      state.tableDisplay = null
      state.tableSetAt = null;
      state.enteredViaQr = false
    },
    SET_ENTERED_VIA_QR(state, value) {
      state.enteredViaQr = value
    },
    SET_PAYMENT_TYPE(state, paymentType) {
      state.paymentType = paymentType
    },
    SET_CART_ANIMATION(state) {
      state.isAnimating = 'pulse'
      const delay = t => new Promise(resolve => setTimeout(resolve, t));
      delay(1000).then(() => state.isAnimating = 'no-pulse')
    },
    CART_ADD_ITEM(state, payload) {
      const { menuItem, storeId } = payload

      const storeCart = storeId
        ? state.carts[ storeId ] || []
        : state.localCart
      const isConfigurable = (_menuItem) => _menuItem.itemAttributeList.length > 0 || // Cannot merge with modifiers
        (_menuItem.childItemList && _menuItem.childItemList.length) ||                // Cannot merge with relations
        !!_menuItem.specialRequest ||                                                  // Cannot merge with specialRequest
        (_menuItem.relations && _menuItem.relations.some(rel => rel.charge && rel.charge.quantityRule)) // Cannot merge quantity-based promotions

      // Attempt to find the same item in the cart, can't merge items with special requests
      const exists = storeCart.find(item => item.menuItem.code === menuItem.code && !isConfigurable(item.menuItem))

      // Add a UUID to all menuItems in our local cart
      menuItem.uuid = generateUUID()
      if (menuItem.childItemList && menuItem.childItemList.length) {
        let childSubTotal = 0;
        let childDiscountTotal = 0;
        let childItemPrice = 0;
        menuItem.childItemList.forEach(child => {
          childSubTotal += Number(child.subTotal || 0);
          childDiscountTotal += Number(child.discountTotal || 0);
          childItemPrice += Number(child.itemPrice || 0);
        });
        if (menuItem.price) childSubTotal += Number(menuItem.price);
        if (menuItem.itemPrice) childItemPrice += Number(menuItem.itemPrice);
        menuItem.subTotal = childSubTotal;
        menuItem.discountTotal = childDiscountTotal;
        menuItem.itemPrice = childItemPrice;
      }

      if (isConfigurable(menuItem) || !exists) {
        if (storeId) {
          const items = state.carts[ storeId ]
          items.push({ menuItem })
          // In order to be reactive, we cannot just modify the array in place; It must be reassigned
          state.carts[ storeId ] = items

          const cloneState = { ...state.carts }
          state.carts = cloneState
        } else{
          state.localCart.push({ menuItem })
        }
      } else {
        exists.menuItem.qty += (menuItem.qty || 1)
      }
    },
    CART_INCREMENT_ITEM(state, payload) {
      const { index, storeId } = payload

      const storeCart = storeId
        ? state.carts[ storeId ] || []
        : state.localCart
      const item = storeCart[index]
      item.menuItem.qty++
    },
    CART_DECREMENT_ITEM(state, payload) {
      const { index, storeId } = payload
      const storeCart = storeId
        ? state.carts[ storeId ] || []
        : state.localCart

      const item = storeCart[index]

      if (item.menuItem.qty === 1 || ('minCardinality' in item.menuItem && item.menuItem.qty <= item.menuItem.minCardinality)) {
        storeCart.splice(index, 1)
      } else {
        item.menuItem.qty--
      }
    },
    CART_REMOVE_ITEM(state, payload) {
      const { menuItem, storeId, cart } = payload
      const storeCart = storeId
        ? state.carts[ storeId ] || []
        : cart || state.localCart

      const exists = storeCart.find(item => item.menuItem.code === menuItem.menuItem.code)

      if (exists && exists.qty >= 1) {
        const index = storeCart.findIndex(item => item.menuItem.code === exists.menuItem.code)

        exists.qty === 1 ? storeCart.splice(index, 1) : exists.qty--
      }
    },
    CART_REPLACE_ITEM(state, payload) {
      const { menuItem, index, storeId } = payload

      if (storeId) {
        const cart = state.carts[ storeId ] || []
        cart.splice(index, 1, { menuItem })
        state.carts[ storeId ] = cart
      } else {
        state.localCart.splice(index, 1, { menuItem })
      }
    },
    SET_ORDER_TYPE(state, orderType) {
      state.orderType = orderType
    },
    SET_LOCAL_CART(state, data) {
      const { cart, storeId } = data

      storeId
        ? state.carts[ storeId ] = cart
        : state.localCart = cart
    },
    SET_SERVER_CART(state, cart) {
      state.serverCart = cart
    },
    // Pickup and Delivery have entirely different valid time slots -- validTimeSlots has no
    // type tag on it, so a slot list fetched for one type is silently reused as if it were
    // valid for the other until a fresh fetch overwrites it. Clearing it the instant a
    // switch begins closes that window, rather than racing to refetch before anything reads it.
    CLEAR_VALID_TIME_SLOTS(state) {
      if (state.serverCart) {
        state.serverCart.validTimeSlots = null
      }
    },
    SET_BILL(state, bill) {
      state.bill = bill
    },
    SET_CART_LOADING(state, loading) {
      state.isAnimating = loading
      state.isCartLoading = loading
    },
    SET_DELIVERY_ADDRESS(state, address) {
      state.deliveryAddress = address
    },
    SET_DELIVERY_ADDRESS_RESULT(state, result) {
      state.deliveryAddressResult = result
    },
    SET_DELIVERY_PROVIDER(state, deliveryProvider) {
      state.deliveryProvider = { ...deliveryProvider }
    },
    SET_DELIVERY_PROVIDERS_DETAILS(state, deliveryProviders) {
      state.deliveryProvidersDetails = [...deliveryProviders]
    },
    RESET_CART(state, payload) {
      const { storeId, tempSlots } = payload

      // if (storeId && `${storeId}` in state.carts) {
      //   state.carts[ storeId ] = []
      //   state.couponCodes[storeId] = null
      // }

      // the above logic always returned the store id as "_"
      // not sure if that is the correct behavior so
      // Resetting all carts as we do not need to keep them in memory
      state.carts = {}
      // DEV-9458: a typed coupon code (CouponInput) belongs to the cart it was entered for --
      // clear it with the carts (all stores, same as above), or it rides into the next order
      // after a completed payment. The selected coupon is cleared by resetCart already.
      state.couponCodes = {}
      state.membershipPayCap = null // DEV-10377: a new order starts from the full balance

      state.localCart = []
      state.serverCart = {}

      state.serverCart.validTimeSlots = tempSlots

      state.bill = {}
      //state.orderType = ''
      // state.tableId = -1
      state.subOrder = {}
      //state.deliveryAddress = null
      state.isAnimating = false
      state.multiOrder = {}
      state.subOrder = {}
      state.orderUUID = generateUUID()
      // rewards/activeRewards are store-specific and were never reset here —
      // once fetched (e.g. during a shared-cart session), they stayed
      // populated forever, including after the session ended. rewardOptions()
      // falls back to showing the full rewards catalog whenever the cart has
      // no live subOrder, so a genuinely empty cart after RESET_CART would
      // render every redeemable reward as if it were cart content.
      state.rewards = []
      state.activeRewards = null
    },
    ENSURE_STORE_CART(state, storeId) {
      const doesNotExist = !(storeId in state.carts)
      if(storeId && doesNotExist){
        state.carts[ storeId ] = []
        state.orderUUID = generateUUID()
      } else {
        // to handle backwards compatibility
        if (isEmpty(state.orderUUID)) {
          state.orderUUID = generateUUID()
        }
      }
    },
    GENERATE_ORDER_UUID(state) {
      state.orderUUID = generateUUID()
    },
    SET_SUB_ORDER(state, order) {
      state.subOrder = {}
      state.multiOrder = {}
      state.multiOrder = order
    },
    SET_IN_STORE(state, inStore) {
      state.inStore = inStore
    },
    SET_SERVICE_CHARGES(state, charges) {
      state.serviceCharges = charges || []
    },

    SET_REWARDS(state, payload) {
      state.rewards = payload
    },
    SET_REWARDS_CONFIG(state, payload) {
      state.rewardsConfig = payload
    },
    SET_ACTIVE_REWARDS(state, payload) {
      state.activeRewards = payload
    },
    SET_PROMOTIONS(state, payload) {
      state.promotions = payload

      // Build lookup maps for O(1) promotion matching
      const byItemCode = {}
      const byTag = {}
      const byCategory = {}

      ;(payload || []).forEach(promo => {
        if (!promo.enabled || promo.orderLevel) return

        const rules = promo.promotionRules || []
        rules.forEach(rule => {
          const targets = rule.targets || []
          const promoEntry = {
            name: promo.name,
            ruleType: rule.type || '',
            discountValue: rule.discountValue || 0,
            percentageBased: rule.percentageBased === true
          }

          targets.forEach(target => {
            const targetCodes = target.targetCodes || []
            const targetType = target.targetType

            if (targetCodes.length === 0) return

            if (targetType === 'ITEM') {
              targetCodes.forEach(code => {
                if (!byItemCode[code]) byItemCode[code] = []
                byItemCode[code].push(promoEntry)
              })
            } else if (targetType === 'TAG') {
              targetCodes.forEach(tag => {
                if (!byTag[tag]) byTag[tag] = []
                byTag[tag].push(promoEntry)
              })
            } else if (targetType === 'CATEGORY') {
              targetCodes.forEach(catCode => {
                // Extract category ID (e.g., "menu_469_categories_889" -> "categories_889")
                const categoryPart = catCode.match(/categories_\d+/)
                if (categoryPart) {
                  const catKey = categoryPart[0]
                  if (!byCategory[catKey]) byCategory[catKey] = []
                  byCategory[catKey].push(promoEntry)
                }
              })
            }
          })
        })
      })

      state.promotionsByItemCode = byItemCode
      state.promotionsByTag = byTag
      state.promotionsByCategory = byCategory
    }
  }
}
